import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import {
  CardDisplaySettings, CardFieldKey, CardFieldStyle, CardPosition, DeckRenderMode, PipelineItem,
  PageDef, PageLayout, SECTION_COLORS, SECTION_ORDER,
} from '../types';
import { DeckPage, DeckHeader, DeckFooter, CANVAS_W, CANVAS_H, useDeckAutoPan } from '../components/DeckPage';
import { DeckCard, CARD_W, DEFAULT_VISIBLE_CARD_FIELDS } from '../components/DeckCard';
import { AdjustableMapLegend, DeckMap, MapRect, mapLegendHeight, mapRectHeight, projectToCanvas } from '../components/DeckMap';
import { clusterMarkerPlacements, hasFiniteCoordinates, resolveMarkerTextCollisions } from '../utils/markerPlacement';
import { defaultLineStyle, orthogonalizeRoute } from '../utils/connectorRouting';

// Keep downstream marker projection identical to Overview.  Both pages now
// use the same map rectangle, so a project marker never shifts when switching
// tabs.
const MAP_WIDTH = 858;
const MAP_RECT: MapRect = {
  x: 371,
  y: 292,
  w: MAP_WIDTH,
};
const MAP_BOTTOM = MAP_RECT.y + mapRectHeight(MAP_RECT);
// Tight protection for the rendered page title/subtitle. This intentionally
// leaves the side lanes open while preventing generated cards from painting
// over the header glyphs.
const CARD_TOP_Y = 170;
// Residential, EC, and Commercial & Industrial have only the main title plus
// one subtitle, so their first card row can sit closer to the heading without
// entering its safe area.
const SINGLE_SUBTITLE_CARD_TOP_Y = 130;
const ACCENT_CARD_TOP_Y = 190;
const TITLE_BOUNDS = { x: 350, y: 8, w: 900, h: CARD_TOP_Y - 8 };
const SINGLE_SUBTITLE_TITLE_BOUNDS = { x: 350, y: 8, w: 900, h: SINGLE_SUBTITLE_CARD_TOP_Y - 8 };
const ACCENT_HEADER_BOUNDS = { x: 300, y: 8, w: 1000, h: ACCENT_CARD_TOP_Y - 8 };
// Keep generated cards and the legend close to the bottom disclaimer, with a
// small visual gap so neither element touches the canvas edge or footer text.
const FOOTER_CLEARANCE = 38;
// Keep a visibly usable corridor between generated cards.
const PERIMETER_GAP = 20;
// Match the card's natural content height so generated cards preserve a
// readable aspect ratio instead of vertically squashing their text.
const CARD_H_EST = 155;
const MIN_CARD_W = 96;
const MIN_CARD_H = 72;
const CARD_ALIGNMENT_GUIDE_TOLERANCE = 5;
const CONNECTOR_STROKE_WIDTH = 2;
const CONNECTOR_SELECTED_STROKE_WIDTH = 4.5;
const MIN_MIDPOINT_HANDLE_LENGTH = 24;
const PIPELINE_PDF_DEBUG = process.env.NODE_ENV !== 'production'
  && process.env.REACT_APP_DEBUG_PIPELINE_PDF === 'true';
// Downstream legends are intentionally compact; the project-name key is
// rendered above the table rather than consuming another row inside it.
const DEFAULT_LEGEND_W = 240;
const DEFAULT_LEGEND = {
  x: CANVAS_W - DEFAULT_LEGEND_W - 18,
  y: CANVAS_H - mapLegendHeight(DEFAULT_LEGEND_W, false, true) - 42,
  w: DEFAULT_LEGEND_W,
};
const CARD_FIELD_OPTIONS: Array<{ key: CardFieldKey; label: string; defaultSize: number; defaultBold?: boolean }> = [
  { key: 'projectName', label: 'Project name', defaultSize: 11.5, defaultBold: true },
  { key: 'roadName', label: 'Road name', defaultSize: 9.5 },
  { key: 'status', label: 'Status', defaultSize: 9.5 },
  { key: 'targetPreview', label: 'Target preview', defaultSize: 9.5, defaultBold: true },
  { key: 'district', label: 'District', defaultSize: 9 },
  { key: 'tenure', label: 'Tenure', defaultSize: 9 },
  { key: 'estUnits', label: 'Estimated units', defaultSize: 9 },
  { key: 'developer', label: 'Developer', defaultSize: 9 },
  { key: 'landCost', label: 'Land cost', defaultSize: 9.5, defaultBold: true },
  { key: 'estLaunchPrice', label: 'Estimated launch price', defaultSize: 9, defaultBold: true },
];

const intersects = (
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/**
 * Return whether two generated card rectangles are closer than the shared
 * perimeter clearance.  Expanding only the candidate rectangle keeps the
 * comparison symmetric for the edge-to-edge distance while avoiding any
 * mutation of saved/manual rectangles.
 */
const violatesCardClearance = (
  candidate: { x: number; y: number; w: number; h: number },
  existing: { x: number; y: number; w: number; h: number },
  gap = PERIMETER_GAP,
) => intersects(
  {
    x: candidate.x - gap / 2,
    y: candidate.y - gap / 2,
    w: candidate.w + gap,
    h: candidate.h + gap,
  },
  existing,
);

type CanvasSide = 'top' | 'right' | 'bottom' | 'left';

type PlacementSlot = {
  key: string;
  side: CanvasSide;
  x: number;
  y: number;
};

function bendCountForStyle(style?: CardPosition['lineStyle']): number {
  if (style === 'one-bend') return 1;
  if (style === 'two-bend') return 2;
  if (style === 'three-bend') return 3;
  if (style === 'four-bend') return 4;
  if (style === 'five-bend') return 5;
  if (style === 'six-bend') return 6;
  if (style === 'seven-bend') return 7;
  return 0;
}

function generatedOrthogonalBends(anchor: RoutePoint, target: RoutePoint, count: number, horizontalFirstOverride?: boolean): RoutePoint[] {
  if (count <= 0) return [];
  const horizontalFirst = horizontalFirstOverride ?? (Math.abs(target.x - anchor.x) >= Math.abs(target.y - anchor.y));
  const midX = anchor.x + (target.x - anchor.x) * 0.5;
  const midY = anchor.y + (target.y - anchor.y) * 0.5;
  const firstX = anchor.x + (target.x - anchor.x) * 0.33;
  const secondX = anchor.x + (target.x - anchor.x) * 0.66;
  const firstY = anchor.y + (target.y - anchor.y) * 0.33;
  const secondY = anchor.y + (target.y - anchor.y) * 0.66;
  const base = count === 1
    ? [horizontalFirst ? { x: target.x, y: anchor.y } : { x: anchor.x, y: target.y }]
    : count === 2
      ? (horizontalFirst
        ? [{ x: midX, y: anchor.y }, { x: midX, y: target.y }]
        : [{ x: anchor.x, y: midY }, { x: target.x, y: midY }])
      : count === 3
        ? (horizontalFirst
          ? [{ x: firstX, y: anchor.y }, { x: firstX, y: midY }, { x: target.x, y: midY }]
          : [{ x: anchor.x, y: firstY }, { x: midX, y: firstY }, { x: midX, y: target.y }])
        : (horizontalFirst
          ? [{ x: firstX, y: anchor.y }, { x: firstX, y: secondY }, { x: secondX, y: secondY }, { x: secondX, y: target.y }]
          : [{ x: anchor.x, y: firstY }, { x: secondX, y: firstY }, { x: secondX, y: secondY }, { x: target.x, y: secondY }]);
  if (count <= 4) return base;
  const tail = base[base.length - 1];
  return [...base, ...Array.from({ length: count - 4 }, (_, index) => {
    const fraction = (index + 1) / (count - 3);
    return { x: tail.x + (target.x - tail.x) * fraction, y: tail.y + (target.y - tail.y) * fraction };
  })];
}

type MapMarker = {
  item: PipelineItem;
  number: number;
  x: number;
  y: number;
  radius: number;
  layoutKey: string;
  itemIds?: number[];
  numbers?: number[];
};

const DOWNSTREAM_MARKER_RADIUS = 6;
function markerLayoutKey(itemIds: number[]): string {
  return `marker:${itemIds.slice().sort((a, b) => a - b).join('-')}`;
}

function previewSortKey(raw: string | null): number {
  if (!raw) return Number.POSITIVE_INFINITY;
  const value = raw.trim().toUpperCase();
  const quarter = value.match(/^(?:Q([1-4])|([1-4])Q)\s*[-/]?\s*(\d{4})$/);
  if (quarter) return new Date(Number(quarter[3]), (Number(quarter[1] || quarter[2]) - 1) * 3, 1).getTime();
  const half = value.match(/^([12])H\s*[-/]?\s*(\d{4})$/);
  if (half) return new Date(Number(half[2]), Number(half[1]) === 1 ? 0 : 6, 1).getTime();
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function compareMarkerItems(a: PipelineItem, b: PipelineItem): number {
  const district = (value: string | null) => {
    const digits = value?.match(/\d+/)?.[0];
    return digits ? Number(digits) : Number.POSITIVE_INFINITY;
  };
  return previewSortKey(a.targetPreview) - previewSortKey(b.targetPreview)
    || district(a.district) - district(b.district)
    || (a.projectName || a.roadName || '').localeCompare(b.projectName || b.roadName || '', undefined, { sensitivity: 'base' })
    || a.id - b.id;
}

type RoutePoint = { x: number; y: number };
type ConnectorStatus = 'connected' | 'missing-coordinates' | 'no-safe-route';

/**
 * Normalize every connector to axis-aligned segments before removing
 * duplicates. Older saved layouts can contain a direct diagonal route when
 * they had no explicit bends, so deduplication alone is not sufficient.
 */
const dedupeRoute = (points: RoutePoint[], preserveCollinear = false): RoutePoint[] => orthogonalizeRoute(points).reduce<RoutePoint[]>((result, point) => {
  const previous = result[result.length - 1];
  if (!previous || Math.abs(previous.x - point.x) >= 0.5 || Math.abs(previous.y - point.y) >= 0.5) {
    result.push(point);
    if (!preserveCollinear) {
      while (result.length >= 3) {
        const before = result[result.length - 3];
        const middle = result[result.length - 2];
        const last = result[result.length - 1];
        const sameHorizontal = Math.abs(before.y - middle.y) < 0.5 && Math.abs(middle.y - last.y) < 0.5;
        const sameVertical = Math.abs(before.x - middle.x) < 0.5 && Math.abs(middle.x - last.x) < 0.5;
        if (!sameHorizontal && !sameVertical) break;
        result.splice(result.length - 2, 1);
      }
    }
  }
  return result;
}, []);

function recalculatedCardBranch(
  anchor: RoutePoint,
  junction: RoutePoint,
  count: number,
  horizontalFirstOverride?: boolean,
): { route: RoutePoint[]; bends: RoutePoint[] } {
  const bends = generatedOrthogonalBends(anchor, junction, count, horizontalFirstOverride);
  return {
    bends,
    route: dedupeRoute([anchor, ...bends, junction], true),
  };
}

interface MapPageProps {
  page: PageDef;
  items: PipelineItem[];
  /** Base project set used to keep marker clustering aligned with Overview. */
  markerReferenceItems?: PipelineItem[];
  layout: PageLayout;
  itemOverrides?: Record<string, Partial<PipelineItem>>;
  regionColorLayout?: PageLayout;
  asAtDate: string;
  editable?: boolean;
  fixed?: boolean;
  onCardMove?: (itemId: number, xPct: number, yPct: number) => void;
  onCardDrop?: (itemId: number, xPct: number, yPct: number) => void;
  onCardResize?: (itemId: number, widthPct: number, heightPct: number, xPct?: number, yPct?: number) => void;
  onLineBend?: (itemId: number, xPct: number, yPct: number) => void;
  onLineLayoutChange?: (itemId: number, patch: Partial<CardPosition>) => void;
  onPageItemChange?: (item: PipelineItem) => void;
  onLegendChange?: (xPct: number, yPct: number, widthPct: number) => void;
  onCardDisplayChange?: (settings: CardDisplaySettings, targetItemIds: number[]) => void;
  onMarkerMove?: (markerKey: string, xPct: number, yPct: number) => void;
  onMarkerLineLayoutChange?: (markerKey: string, patch: Partial<CardPosition>) => void;
  onMarkerBend?: (markerKey: string, bendIndex: number, xPct: number, yPct: number) => void;
  onGeneratedCardPositions?: (positions: Record<string, { x: number; y: number }>) => void;
  /** Signals that this page's connector setup is ready for export. */
  onRoutingComplete?: (pageKey: PageDef['key'], routes: CachedConnectorRoutes) => void;
  cachedRoutes?: CachedConnectorRoutes;
  /** Incremented by an explicit placement reset to request one new route pass. */
  routingGeneration?: number;
  /** Presentation-only connector stroke width for this downstream page. */
  connectorStrokeWidth?: number;
  cardSettingsOpen?: boolean;
  onCardSettingsClose?: () => void;
  backgroundImage?: string;
  multiSelectMode?: boolean;
  /** Incremented by the toolbar to select every card on this page. */
  selectAllRequest?: number;
  renderCards?: boolean;
  renderConnectors?: boolean;
  renderMode?: DeckRenderMode;
}

export type CachedConnectorRoutes = {
  generation: number;
  identity?: string;
  complete: boolean;
  routes: Record<number, RoutePoint[]>;
  statuses: Record<number, ConnectorStatus>;
  reasons: Record<number, string>;
};

export const MapPage: React.FC<MapPageProps> = ({
  page,
  items,
  markerReferenceItems,
  layout,
  itemOverrides = {},
  regionColorLayout = {},
  asAtDate,
  editable = false,
  fixed = false,
  onCardMove,
  onCardDrop,
  onCardResize,
  onLineLayoutChange,
  onPageItemChange,
  onLegendChange,
  onCardDisplayChange,
  onMarkerMove,
  onMarkerLineLayoutChange,
  onMarkerBend,
  onGeneratedCardPositions,
  onRoutingComplete,
  routingGeneration = 0,
  connectorStrokeWidth = CONNECTOR_STROKE_WIDTH,
  cardSettingsOpen = false,
  onCardSettingsClose,
  backgroundImage,
  multiSelectMode = false,
  selectAllRequest = 0,
  renderCards = false,
  renderConnectors = false,
  renderMode = 'interactive',
}) => {
  const autoPan = useDeckAutoPan();
  const usesRaisedFirstRow = page.key === 'residential'
    || page.key === 'ec'
    || page.key === 'commercial-industrial';
  const pageCardTopY = page.accent
    ? ACCENT_CARD_TOP_Y
    : usesRaisedFirstRow
      ? SINGLE_SUBTITLE_CARD_TOP_Y
      : CARD_TOP_Y;
  const pageHeaderBounds = page.accent
    ? ACCENT_HEADER_BOUNDS
    : usesRaisedFirstRow
      ? SINGLE_SUBTITLE_TITLE_BOUNDS
      : TITLE_BOUNDS;
  const effectiveConnectorStrokeWidth = Math.max(1, Math.min(6, Number(connectorStrokeWidth) || CONNECTOR_STROKE_WIDTH));
  const [lineMenu, setLineMenu] = useState<{ itemId: number; top: number; left: number } | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [cardHeights, setCardHeights] = useState<Record<number, number>>({});
  const [printCardMeasurements, setPrintCardMeasurements] = useState<Record<number, number>>({});
  const [fontSizeDrafts, setFontSizeDrafts] = useState<Partial<Record<CardFieldKey, string>>>({});
  const [selectedCardIds, setSelectedCardIds] = useState<Set<number>>(new Set());
  const [cardAlignmentGuides, setCardAlignmentGuides] = useState<{
    vertical?: number;
    horizontal?: number;
  } | null>(null);
  // A shared marker fork is one editable object even though its coordinates
  // are persisted on each member card.  Keep a transient value while the
  // handle is being dragged so the junction follows the pointer immediately.
  const [forkOverrides, setForkOverrides] = useState<Map<number, RoutePoint>>(new Map());
  // Marker-side previews are shared by marker group, never stored on an
  // individual card route. Card routes may still have local previews, but
  // their marker-side portion is ignored while this map has an entry.
  const [sharedTailPreviews, setSharedTailPreviews] = useState<Map<number, RoutePoint[]>>(new Map());
  const [markerDragOverrides, setMarkerDragOverrides] = useState<Map<string, RoutePoint>>(new Map());
  const forkDragRef = useRef<{
    markerId: number;
    memberIds: number[];
    startX: number;
    startY: number;
    origin: RoutePoint;
    originalLocalRoutes: Map<number, RoutePoint[]>;
  } | null>(null);
  // Capture the positions at the beginning of a multi-card drag.  Using the
  // current card position for every pointer event makes the delta incremental
  // and can leave selected cards behind when React batches updates.
  const multiDragRef = useRef<{
    draggedId: number;
    originDragged: RoutePoint;
    origins: Map<number, RoutePoint>;
  } | null>(null);
  const multiResizeRef = useRef<{
    resizedId: number;
    geometries: Map<number, { x: number; y: number; w: number; h: number }>;
  } | null>(null);
  const cardDisplay = layout['card:settings']?.cardDisplay ?? {};
  const selectedCardIdList = Array.from(selectedCardIds);
  // Card multi-selection is still used for group movement and resizing, but
  // connector editing has one active card at a time. This prevents a sibling
  // branch from being promoted into the highlighted connector layer.
  const activeConnectorCardId = selectedCardIdList.length
    ? selectedCardIdList[selectedCardIdList.length - 1]
    : null;
  const mergeCardDisplay = (override?: CardDisplaySettings): CardDisplaySettings => {
    const sharedFields = cardDisplay.fields ?? {};
    const overrideFields = override?.fields ?? {};
    const keys = new Set([...Object.keys(sharedFields), ...Object.keys(overrideFields)] as CardFieldKey[]);
    const fields: Partial<Record<CardFieldKey, CardFieldStyle>> = {};
    keys.forEach((key) => {
      const merged = { ...(sharedFields[key] ?? {}), ...(overrideFields[key] ?? {}) } as Record<string, unknown>;
      Object.keys(merged).forEach((field) => {
        if (merged[field] === undefined) delete merged[field];
      });
      fields[key] = merged;
    });
    return { ...cardDisplay, fields };
  };
  const cardDisplayForItem = (itemId: number) => mergeCardDisplay(layout[String(itemId)]?.cardDisplay);
  const patchCardField = (key: CardFieldKey, patch: Record<string, unknown>) => {
    if (!selectedCardIdList.length) return;
    onCardDisplayChange?.({ fields: { [key]: patch } }, selectedCardIdList);
  };
  const commitCardFontSize = (key: CardFieldKey, raw: string, fallback: number) => {
    const trimmed = raw.trim();
    const parsed = trimmed ? Number(trimmed) : Number.NaN;
    if (Number.isFinite(parsed)) {
      const normalized = Math.round(Math.min(24, Math.max(6, parsed)) * 10) / 10;
      if (normalized !== fallback || selectedCardIdList.length > 1) patchCardField(key, { fontSize: normalized });
    }
    setFontSizeDrafts((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  useEffect(() => {
    if (!cardSettingsOpen) setFontSizeDrafts({});
  }, [cardSettingsOpen]);
  useEffect(() => {
    setEditingItemId(null);
    setLineMenu(null);
    setSelectedCardIds(new Set());
    setPrintCardMeasurements({});
    setForkOverrides(new Map());
    setSharedTailPreviews(new Map());
    setMarkerDragOverrides(new Map());
    setCardAlignmentGuides(null);
    forkDragRef.current = null;
    multiDragRef.current = null;
    multiResizeRef.current = null;
  }, [page.key]);

  const selectCard = useCallback((id: number, additive: boolean, phase: 'down' | 'click' = 'down', wasSelected = false) => {
    setSelectedCardIds((current) => {
      if (!multiSelectMode) return new Set([id]);
      // Select on pointer-down so dragging a card/group starts with the right
      // selection. A plain click on a card that was already selected is
      // resolved on pointer-up and removes it without affecting dragging.
      if (phase === 'click') {
        // A normal click selects only the clicked card. Shift-click remains
        // the explicit multi-selection gesture; without it, sibling card
        // branches must not stay highlighted from an earlier selection.
        if (!additive) return new Set([id]);
        if (wasSelected && current.has(id)) {
          const next = new Set(current);
          next.delete(id);
          return next;
        }
        return new Set(current).add(id);
      }
      if (additive) {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }
      if (current.has(id)) return current;
      return new Set([id]);
    });
  }, [multiSelectMode]);
  const pageItems = useMemo(
    () =>
      items
        .filter((i) => page.sections?.includes(i.section))
        .map((item) => ({ ...item, ...(itemOverrides[String(item.id)] || {}) }))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
    [items, itemOverrides, page.sections],
  );
  const pageItemsRef = useRef(pageItems);
  pageItemsRef.current = pageItems;
  useEffect(() => {
    if (selectAllRequest > 0) setSelectedCardIds(new Set(pageItemsRef.current.map((item) => item.id)));
    // This is intentionally a one-shot toolbar request. Do not depend on
    // pageItems or layout geometry, otherwise moving/resizing a card would
    // reselect cards that the user deliberately removed from the selection.
  }, [selectAllRequest]);

  const regionColor = useCallback(
    (section: PipelineItem['section']) => regionColorLayout[`color:${section}`]?.color ?? SECTION_COLORS[section].bg,
    [regionColorLayout],
  );

  const handleCardSizeChange = useCallback((id: number, height: number) => {
    setCardHeights((current) => current[id] === height ? current : { ...current, [id]: height });
  }, []);

  const handlePrintMeasure = useCallback((id: number, height: number) => {
    if (!Number.isFinite(height) || height <= 0) return;
    setPrintCardMeasurements((current) => current[id] === height ? current : { ...current, [id]: height });
  }, []);

  const dots = useMemo(() => {
    const map = new Map<number, { x: number; y: number } | null>();
    // Keep coordinates for the complete deck so clustered marker centres are
    // identical to Overview even when this page contains only a subset.
    for (const item of items) {
      map.set(
        item.id,
        hasFiniteCoordinates(item.latitude, item.longitude)
          ? projectToCanvas(MAP_RECT, Number(item.longitude), Number(item.latitude))
          : null,
      );
    }
    return map;
  }, [items]);

  // Keep newly generated cards readable while allowing dense pages to fit
  // around the map. Explicitly saved card dimensions remain authoritative;
  // this scale only affects cards that have not been resized yet.  It uses
  // marker lanes rather than only the total item count, so a crowded corner
  // shrinks enough to make room before cards are routed to the map.
  const defaultCardScale = useMemo(() => {
    if (!pageItems.length) return 1;
    const minimumScale = Math.max(MIN_CARD_W / CARD_W, MIN_CARD_H / CARD_H_EST);
    const markerFor = (item: PipelineItem) => dots.get(item.id);
    const groups = new Map<string, PipelineItem[]>();
    pageItems.forEach((item) => {
      const dot = markerFor(item);
      const key = dot ? `${Math.round(dot.x)}:${Math.round(dot.y)}` : `none:${item.id}`;
      groups.set(key, [...(groups.get(key) ?? []), item]);
    });
    const sideFor = (group: PipelineItem[]): CanvasSide => {
      const section = group[0].section;
      if (section === 'EAST') return 'right';
      if (section === 'WEST' || section === 'NORTH' || section === 'NORTH_EAST' || section === 'EC') return 'top';
      return 'bottom';
    };
    const coordinateFor = (group: PipelineItem[], lane: CanvasSide) => {
      const dot = markerFor(group[0]);
      if (!dot) return Number.POSITIVE_INFINITY;
      return lane === 'left' || lane === 'right' ? dot.y : dot.x;
    };
    const canPlaceAll = (scale: number) => {
      const width = CARD_W * scale;
      const height = Math.max(MIN_CARD_H, CARD_H_EST * scale);
      const left = 14; const right = CANVAS_W - width - 14;
      const top = pageCardTopY; const bottom = CANVAS_H - FOOTER_CLEARANCE - height;
      if (right < left || bottom < top) return false;
      const legend = { x: CANVAS_W - DEFAULT_LEGEND_W - 18, y: CANVAS_H - mapLegendHeight(DEFAULT_LEGEND_W, false, true) - 64, w: DEFAULT_LEGEND_W, h: mapLegendHeight(DEFAULT_LEGEND_W, false, true) };
      const markerBlocks = Array.from(dots.values()).filter((dot): dot is { x: number; y: number } => dot != null)
        .map((dot) => ({ x: dot.x - 8, y: dot.y - 8, w: 16, h: 16 }));
      const mapBlock = { x: MAP_RECT.x, y: MAP_RECT.y, w: MAP_RECT.w, h: mapRectHeight(MAP_RECT) };
      const furnitureSafe = (box: { x: number; y: number; w: number; h: number }) =>
        !intersects(box, pageHeaderBounds) && !intersects(box, legend)
        && box.y >= top && box.y + box.h <= CANVAS_H - FOOTER_CLEARANCE
        && box.x >= 0 && box.x + box.w <= CANVAS_W;
      const slots: PlacementSlot[] = [];
      const slotCoordinates = new Set<string>();
      const addSlot = (slot: PlacementSlot) => {
        const coordinateKey = `${Math.round(slot.x)}:${Math.round(slot.y)}`;
        if (slotCoordinates.has(coordinateKey)) return;
        slotCoordinates.add(coordinateKey);
        slots.push(slot);
      };
      for (let x = left; x <= right + 0.5; x += width + PERIMETER_GAP) {
        const slotX = Math.min(right, x);
        addSlot({ key: `top:${Math.round(slotX)}`, side: 'top', x: slotX, y: top });
        if (!(x + width > legend.x && x < legend.x + legend.w)) addSlot({ key: `bottom:${Math.round(slotX)}`, side: 'bottom', x: slotX, y: bottom });
      }
      for (let y = top; y <= bottom + 0.5; y += height + PERIMETER_GAP) {
        const slotY = Math.min(bottom, y);
        addSlot({ key: `left:${Math.round(slotY)}`, side: 'left', x: left, y: slotY });
        addSlot({ key: `right:${Math.round(slotY)}`, side: 'right', x: right, y: slotY });
      }
      const ordered = Array.from(groups.values()).sort((a, b) => {
        const sa = sideFor(a); const sb = sideFor(b);
        return sa.localeCompare(sb) || coordinateFor(a, sa) - coordinateFor(b, sb) || a[0].id - b[0].id;
      });
      const used = new Set<string>();
      const simulatedBoxes: Array<{ x: number; y: number; w: number; h: number }> = [];
      const markerSafe = (slot: PlacementSlot) => !markerBlocks.some((marker) => intersects(
        { x: slot.x, y: slot.y, w: width, h: height }, marker,
      ));
      let placed = 0;
      ordered.forEach((group) => {
        group.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id).forEach(() => {
          const slot = slots.find((candidate) => {
            const box = { x: candidate.x, y: candidate.y, w: width, h: height };
            return !used.has(candidate.key) && furnitureSafe(box) && markerSafe(candidate)
              && !simulatedBoxes.some((entry) => violatesCardClearance(box, entry));
          });
          if (slot) {
            used.add(slot.key); placed += 1;
            simulatedBoxes.push({ x: slot.x, y: slot.y, w: width, h: height });
          }
        });
      });
      if (placed === pageItems.length) return true;
      let interiorPlaced = 0;
      for (let y = top; y <= bottom + 0.5 && placed + interiorPlaced < pageItems.length; y += height + PERIMETER_GAP) {
        for (let x = left; x <= right + 0.5 && placed + interiorPlaced < pageItems.length; x += width + PERIMETER_GAP) {
          const box = { x: Math.min(right, x), y: Math.min(bottom, y), w: width, h: height };
          if (!furnitureSafe(box)
            || markerBlocks.some((marker) => intersects(box, marker))
            || intersects(box, mapBlock)
            || simulatedBoxes.some((entry) => violatesCardClearance(box, entry))) continue;
          simulatedBoxes.push(box); interiorPlaced += 1;
        }
      }
      return placed + interiorPlaced >= pageItems.length;
    };
    if (canPlaceAll(1)) return 1;
    let low = minimumScale; let high = 1;
    for (let iteration = 0; iteration < 20; iteration += 1) {
      const middle = (low + high) / 2;
      if (canPlaceAll(middle)) low = middle;
      else high = middle;
    }
    return low;
  }, [dots, pageCardTopY, pageItems, pageHeaderBounds]);
  const defaultCardWidth = CARD_W * defaultCardScale;
  const defaultCardHeight = Math.max(MIN_CARD_H, CARD_H_EST * defaultCardScale);

  // Use one marker per card at its projected geographic position. Downstream
  // markers intentionally omit the Overview project number.
  const positionedMarkers = useMemo<MapMarker[]>(() => {
    const markerSourceItems = markerReferenceItems ?? items;
    const ordered = [...markerSourceItems].sort(compareMarkerItems);
    // Numbering follows Overview: section order first, then preview date,
    // district, and project name. A marker therefore keeps the same number
    // when the user switches between Overview and a downstream page.
    const allOrdered: PipelineItem[] = [];
    SECTION_ORDER.forEach((section) => {
      items.filter((item) => item.section === section).sort(compareMarkerItems).forEach((item) => allOrdered.push(item));
    });
    const numberById = new Map(allOrdered.map((item, index) => [item.id, index + 1]));
    const bounds = {
      left: MAP_RECT.x,
      right: MAP_RECT.x + MAP_RECT.w,
      top: MAP_RECT.y,
      bottom: MAP_BOTTOM,
    };
    const clamp = (marker: MapMarker): MapMarker => ({
      ...marker,
      x: Math.max(bounds.left + marker.radius, Math.min(bounds.right - marker.radius, marker.x)),
      y: Math.max(bounds.top + marker.radius, Math.min(bounds.bottom - marker.radius, marker.y)),
    });
    const placed: MapMarker[] = [];
    for (const item of ordered) {
      const dot = dots.get(item.id);
      const number = numberById.get(item.id);
      if (!dot) continue;
      placed.push(clamp({ item, number: number ?? 0, ...dot, radius: DOWNSTREAM_MARKER_RADIUS, layoutKey: markerLayoutKey([item.id]) }));
    }
    const clustered = clusterMarkerPlacements(placed.map((marker) => ({
      itemId: marker.item.id,
      number: marker.number,
      x: marker.x,
      y: marker.y,
      radius: marker.radius,
    })));
    const resolvedClusters = resolveMarkerTextCollisions(
      clustered.map((cluster) => ({ ...cluster, radius: DOWNSTREAM_MARKER_RADIUS })),
      bounds,
    );
    const pageIds = new Set(pageItems.map((item) => item.id));
    // Cluster against the complete project set so a downstream page uses the
    // exact same marker centre as Overview. A cluster is rendered on this
    // page when at least one of its projects belongs to the page.
    return resolvedClusters.filter((cluster) => cluster.itemIds.some((id) => pageIds.has(id))).map((cluster) => {
      const itemIds = cluster.itemIds.slice().sort((a, b) => a - b);
      const layoutKey = markerLayoutKey(itemIds);
      const saved = layout[layoutKey];
      const transient = markerDragOverrides.get(layoutKey);
      const x = transient?.x ?? (saved?.x != null ? saved.x / 100 * CANVAS_W : cluster.x);
      const y = transient?.y ?? (saved?.y != null ? saved.y / 100 * CANVAS_H : cluster.y);
      return {
        item: placed.find((marker) => pageIds.has(marker.item.id) && cluster.itemIds.includes(marker.item.id))?.item
          ?? placed.find((marker) => marker.item.id === cluster.itemIds[0])?.item
          ?? placed[0].item,
        number: cluster.number,
        numbers: cluster.numbers,
        itemIds,
        x: Math.max(bounds.left + DOWNSTREAM_MARKER_RADIUS, Math.min(bounds.right - DOWNSTREAM_MARKER_RADIUS, x)),
        y: Math.max(bounds.top + DOWNSTREAM_MARKER_RADIUS, Math.min(bounds.bottom - DOWNSTREAM_MARKER_RADIUS, y)),
        radius: DOWNSTREAM_MARKER_RADIUS,
        layoutKey,
      };
    });
  }, [dots, items, layout, markerDragOverrides, markerReferenceItems, pageItems]);

  const linkedDots = useMemo(() => {
    const linked = new Map<number, { x: number; y: number }>();
    positionedMarkers.forEach((marker) => {
      (marker.itemIds ?? [marker.item.id]).forEach((itemId) => linked.set(itemId, { x: marker.x, y: marker.y }));
    });
    return linked;
  }, [positionedMarkers]);

  const savedLegend = layout.legend;
  const legendX = savedLegend?.x != null ? savedLegend.x / 100 * CANVAS_W : DEFAULT_LEGEND.x;
  const legendY = savedLegend?.y != null ? savedLegend.y / 100 * CANVAS_H : DEFAULT_LEGEND.y;
  const legendW = savedLegend?.width != null ? savedLegend.width / 100 * CANVAS_W : DEFAULT_LEGEND.w;
  const generatedPositionsRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const previousPlacementGenerationRef = useRef(routingGeneration);
  const placementIdentity = `${page.key}:${pageItems.map((item) => item.id).join(',')}`;
  const previousPlacementIdentityRef = useRef(placementIdentity);
  if (previousPlacementIdentityRef.current !== placementIdentity) {
    generatedPositionsRef.current = new Map();
    previousPlacementIdentityRef.current = placementIdentity;
  }
  if (previousPlacementGenerationRef.current !== routingGeneration) {
    generatedPositionsRef.current = new Map();
    previousPlacementGenerationRef.current = routingGeneration;
  }
  const positions = useMemo(() => {
    // Fresh placement allocator: consume explicit perimeter lanes first,
    // ordered by each marker's nearest map edge. Cards sharing a marker are
    // consecutive in the same lane; only when all perimeter slots are used do
    // we fall back to an interior grid.
    const fresh = new Map<number, { x: number; y: number }>();
    const occupiedFresh: Array<{ x: number; y: number; w: number; h: number }> = [];
    const footerY = CANVAS_H - FOOTER_CLEARANCE;
    const legendBoundsFresh = { x: legendX, y: legendY, w: legendW, h: mapLegendHeight(legendW, false, true) };
    const mapBoundsFresh = { x: MAP_RECT.x, y: MAP_RECT.y, w: MAP_RECT.w, h: mapRectHeight(MAP_RECT) };
    const markerBoundsFresh = positionedMarkers.map((marker) => ({
      x: marker.x - marker.radius - 2,
      y: marker.y - marker.radius - 2,
      w: marker.radius * 2 + 4,
      h: marker.radius * 2 + 4,
    }));
    const sizeOf = (item: PipelineItem) => {
      const saved = layout[String(item.id)];
      return { w: saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth, h: saved?.height != null ? saved.height / 100 * CANVAS_H : defaultCardHeight };
    };
    pageItems.forEach((item) => {
      const saved = layout[String(item.id)];
      const cached = generatedPositionsRef.current.get(item.id);
      if (saved?.x == null || saved?.y == null) {
        if (!cached) return;
        fresh.set(item.id, cached);
        occupiedFresh.push({ ...cached, ...sizeOf(item) });
      } else {
        const pos = { x: saved.x / 100 * CANVAS_W, y: saved.y / 100 * CANVAS_H };
        fresh.set(item.id, pos); occupiedFresh.push({ ...pos, ...sizeOf(item) });
        generatedPositionsRef.current.delete(item.id);
      }
    });
    const markerKey = (item: PipelineItem) => {
      const marker = positionedMarkers.find((m) => (m.itemIds ?? [m.item.id]).includes(item.id));
      return marker ? `${Math.round(marker.x)}:${Math.round(marker.y)}` : `none:${item.id}`;
    };
    const groups = new Map<string, PipelineItem[]>();
    // Only allocate cards that do not already have a saved or generated
    // position.  This allocator runs again whenever the layout changes (for
    // example while dragging one card); re-processing positioned cards here
    // would make every other card look as if it disappeared or jumped to a
    // different slot.  Existing geometry is authoritative after generation.
    pageItems
      .filter((item) => !fresh.has(item.id))
      .forEach((item) => groups.set(markerKey(item), [...(groups.get(markerKey(item)) ?? []), item]));
    const markerFor = (group: PipelineItem[]) => positionedMarkers.find((m) => (m.itemIds ?? [m.item.id]).includes(group[0].id));
    const edge = (group: PipelineItem[]) => {
      const marker = markerFor(group); if (!marker) return Number.POSITIVE_INFINITY;
      return Math.min(marker.x - MAP_RECT.x, MAP_RECT.x + MAP_RECT.w - marker.x, marker.y - MAP_RECT.y, MAP_BOTTOM - marker.y);
    };
    const side = (group: PipelineItem[]): CanvasSide => {
      const marker = markerFor(group);
      const section = group[0].section;
      const sectionSide: CanvasSide = section === 'EAST'
        ? 'right'
        : section === 'WEST'
          ? 'left'
          : section === 'NORTH' || section === 'NORTH_EAST' || section === 'EC'
            ? 'top'
            : 'bottom';
      if (!marker) return sectionSide;

      // Use the marker's direction from the map centre rather than the
      // nearest map edge. The nearest-edge rule can place a right-side pin in
      // a top/bottom lane, forcing its connector to route around the map
      // before reaching the card. A dominant-axis placement keeps the card on
      // the same side as its pin and produces the shortest visual connector.
      const mapCentre = {
        x: MAP_RECT.x + MAP_RECT.w / 2,
        y: MAP_RECT.y + mapRectHeight(MAP_RECT) / 2,
      };
      const dx = marker.x - mapCentre.x;
      const dy = marker.y - mapCentre.y;
      if (Math.abs(dx) > Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
      if (Math.abs(dy) > Math.abs(dx)) return dy < 0 ? 'top' : 'bottom';
      return sectionSide;
    };
    const left = 14; const right = CANVAS_W - defaultCardWidth - 14; const top = pageCardTopY; const bottom = footerY - defaultCardHeight;
    const lanes = new Map<CanvasSide, PlacementSlot[]>([['top', []], ['right', []], ['bottom', []], ['left', []]]);
    for (let x = left; x <= right; x += defaultCardWidth + PERIMETER_GAP) {
      lanes.get('top')!.push({ key: `top:${x}`, side: 'top', x, y: top });
      if (!(x + defaultCardWidth > legendBoundsFresh.x && x < legendBoundsFresh.x + legendBoundsFresh.w)) lanes.get('bottom')!.push({ key: `bottom:${x}`, side: 'bottom', x, y: bottom });
    }
    for (let y = top; y <= bottom; y += defaultCardHeight + PERIMETER_GAP) {
      lanes.get('left')!.push({ key: `left:${y}`, side: 'left', x: left, y });
      lanes.get('right')!.push({ key: `right:${y}`, side: 'right', x: right, y });
    }
    const used = new Set<string>();
    const safe = (item: PipelineItem, slot: PlacementSlot) => {
      const box = { x: slot.x, y: slot.y, ...sizeOf(item) };
      return box.y >= top && box.y + box.h <= footerY && box.x >= 0 && box.x + box.w <= CANVAS_W
        && !intersects(box, pageHeaderBounds) && !intersects(box, legendBoundsFresh)
        && !markerBoundsFresh.some((marker) => intersects(box, marker))
        // Perimeter cards are allowed to sit over the map edge when the
        // border lane is the only usable space. Interior cards stay outside
        // the map artwork whenever a safe slot is available.
        && ((!slot.key.startsWith('inner:') && !slot.key.startsWith('fallback:'))
          || !intersects(box, mapBoundsFresh))
        && !occupiedFresh.some((entry) => violatesCardClearance(box, entry));
    };
    const sideRank: Record<CanvasSide, number> = { top: 0, right: 1, bottom: 2, left: 3 };
    const topSectionRank = (group: PipelineItem[]) => {
      const section = group[0].section;
      // Reserve the top-right end of the top lane for North-East projects,
      // then North, before filling the centre with EC/other top-facing groups.
      if (section === 'NORTH_EAST') return 0;
      if (section === 'NORTH') return 1;
      if (section === 'EC') return 2;
      return 3;
    };
    const laneCoordinate = (group: PipelineItem[], lane: CanvasSide) => {
      const marker = markerFor(group);
      if (!marker) return Number.POSITIVE_INFINITY;
      return lane === 'left' || lane === 'right' ? marker.y : marker.x;
    };
    const allGroups = Array.from(groups.values()).sort((a, b) => {
      const sa = side(a); const sb = side(b);
      if (sa === 'top' && sb === 'top') {
        const sectionOrder = topSectionRank(a) - topSectionRank(b);
        if (sectionOrder !== 0) return sectionOrder;
      }
      return sideRank[sa] - sideRank[sb]
        || laneCoordinate(a, sa) - laneCoordinate(b, sb)
        || edge(a) - edge(b)
        || a[0].id - b[0].id;
    });
    const placedGroups = new Set<string>();
    const markerGroupId = (group: PipelineItem[]) => markerKey(group[0]);
    const placeGroup = (group: PipelineItem[], candidates: PlacementSlot[]) => {
      if (!candidates.length) return false;
      for (let start = 0; start <= candidates.length - group.length; start += 1) {
        const selected = candidates.slice(start, start + group.length);
        const temporary: Array<{ x: number; y: number; w: number; h: number }> = [];
        let valid = true;
        selected.forEach((slot, index) => {
          if (used.has(slot.key) || !safe(group[index], slot)) valid = false;
          const box = { x: slot.x, y: slot.y, ...sizeOf(group[index]) };
          if (temporary.some((entry) => violatesCardClearance(box, entry))) valid = false;
          temporary.push(box);
        });
        if (!valid) continue;
        selected.forEach((slot, index) => {
          used.add(slot.key);
          const pos = { x: slot.x, y: slot.y };
          fresh.set(group[index].id, pos);
          generatedPositionsRef.current.set(group[index].id, pos);
          occupiedFresh.push({ ...pos, ...sizeOf(group[index]) });
        });
        placedGroups.add(markerGroupId(group));
        return true;
      }
      return false;
    };
    // Claim the four deck corners for the four geographically extreme marker
    // groups before consuming ordinary edge slots.
    const cornerDefs: Array<{ x: number; y: number; side: CanvasSide; sections: PipelineItem['section'][] }> = [
      { x: left, y: top, side: 'top', sections: ['WEST'] },
      { x: right, y: top, side: 'top', sections: ['NORTH', 'NORTH_EAST'] },
      { x: (left + right) / 2, y: top, side: 'top', sections: ['EC'] },
      { x: right, y: bottom, side: 'right', sections: ['EAST'] },
    ];
    cornerDefs.forEach((corner) => {
      const available = allGroups.filter((group) => !placedGroups.has(markerGroupId(group))
        && markerFor(group) && corner.sections.includes(group[0].section));
      const winner = available.sort((a, b) => {
        const ma = markerFor(a)!; const mb = markerFor(b)!;
        return Math.hypot(ma.x - corner.x, ma.y - corner.y) - Math.hypot(mb.x - corner.x, mb.y - corner.y)
          || markerGroupId(a).localeCompare(markerGroupId(b));
      })[0];
      if (!winner) return;
      const reverse = corner.x === right;
      const slots = Array.from({ length: winner.length }, (_, index) => ({
        key: `corner:${corner.side}:${corner.x}:${corner.y}:${index}`,
        side: corner.side,
        x: reverse ? corner.x - index * (defaultCardWidth + PERIMETER_GAP) : corner.x + index * (defaultCardWidth + PERIMETER_GAP),
        y: corner.y,
      }));
      placeGroup(winner, slots);
    });
    allGroups.filter((group) => !placedGroups.has(markerGroupId(group))).forEach((group) => {
      const preferred = side(group);
      const sides = [preferred, 'top', 'right', 'bottom', 'left'].filter((s, i, all) => all.indexOf(s) === i) as CanvasSide[];
      const topLane = (group[0].section === 'NORTH' || group[0].section === 'NORTH_EAST')
        ? [...(lanes.get('top') ?? [])].reverse()
        : (lanes.get('top') ?? []);
      const laneCandidates = preferred === 'top'
        ? [...topLane, ...sides.filter((s) => s !== 'top').flatMap((s) => lanes.get(s) ?? [])]
        : sides.flatMap((s) => lanes.get(s) ?? []);
      if (preferred === 'top' && group[0].section === 'EC') {
        const center = (left + right) / 2;
        const centeredTop = topLane.slice().sort((a, b) => Math.abs(a.x - center) - Math.abs(b.x - center));
        laneCandidates.splice(0, topLane.length, ...centeredTop);
      }
      // Projects sharing one map marker must occupy adjacent slots. Otherwise
      // each card can independently fall into a different lane and the
      // connectors are forced to cross the other cards in the group.
      group.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
      if (placeGroup(group, laneCandidates)) return;
      group.forEach((item) => {
        const slot = laneCandidates.find((candidate) => !used.has(candidate.key) && safe(item, candidate));
        let chosen = slot;
        if (!chosen) {
          for (let y = top; !chosen && y <= bottom; y += defaultCardHeight + PERIMETER_GAP) for (let x = left; x <= right; x += defaultCardWidth + PERIMETER_GAP) {
            const candidate: PlacementSlot = { key: `inner:${x}:${y}`, side: 'bottom', x, y };
            if (safe(item, candidate)) chosen = candidate;
          }
        }
        // A blocked map/legend can leave no slot on the coarse pitch. Search
        // the remaining canvas at a finer deterministic step before falling
        // back; this keeps the same 20px edge-to-edge clearance even when a
        // marker cuts through an otherwise usable grid cell.
        if (!chosen) {
          const { w, h } = sizeOf(item);
          const step = Math.max(4, Math.min(w, h) / 4);
          for (let y = top; !chosen && y <= footerY - h + 0.5; y += step) {
            for (let x = 0; !chosen && x <= CANVAS_W - w + 0.5; x += step) {
              const candidate: PlacementSlot = { key: `inner:dense:${Math.round(x)}:${Math.round(y)}`, side: 'bottom', x, y };
              if (safe(item, candidate)) chosen = candidate;
            }
          }
        }
        // If map/legend furniture blocks every protected slot, make one more
        // search that relaxes only the furniture restriction. Generated cards
        // must still never overlap one another; occupying open map space is
        // preferable to stacking cards on top of cards.
        if (!chosen) {
          const { w, h } = sizeOf(item);
          const step = Math.max(4, Math.min(w, h) / 4);
          for (let y = top; !chosen && y <= footerY - h + 0.5; y += step) {
            for (let x = 0; !chosen && x <= CANVAS_W - w + 0.5; x += step) {
              const candidate: PlacementSlot = { key: `open:dense:${Math.round(x)}:${Math.round(y)}`, side: 'bottom', x, y };
              const box = { x, y, w, h };
              if (x < 0 || y < top || x + w > CANVAS_W || y + h > footerY) continue;
              if (occupiedFresh.some((entry) => violatesCardClearance(box, entry))) continue;
              chosen = candidate;
            }
          }
        }
        // The adaptive scale normally guarantees a safe slot. If protected
        // furniture and the canvas are mathematically full, retain the
        // historical bounded fallback rather than dropping the project card.
        chosen ??= { key: `fallback:${item.id}`, side: preferred, x: left, y: top };
        used.add(chosen.key); const pos = { x: chosen.x, y: chosen.y }; fresh.set(item.id, pos); generatedPositionsRef.current.set(item.id, pos); occupiedFresh.push({ ...pos, ...sizeOf(item) });
      });
    });
    return fresh;
  }, [layout, pageItems, legendX, legendY, legendW, positionedMarkers, defaultCardWidth, defaultCardHeight, pageCardTopY, pageHeaderBounds]);

  const reportedGeneratedPositionsRef = useRef('');
  useEffect(() => {
    if (!onGeneratedCardPositions) return;
    const generated: Record<string, { x: number; y: number }> = {};
    pageItems.forEach((item) => {
      const saved = layout[String(item.id)];
      const position = positions.get(item.id);
      if (saved?.x != null || saved?.y != null || !position) return;
      generated[String(item.id)] = {
        x: position.x / CANVAS_W * 100,
        y: position.y / CANVAS_H * 100,
      };
    });
    const signature = JSON.stringify(generated);
    if (signature === reportedGeneratedPositionsRef.current) return;
    reportedGeneratedPositionsRef.current = signature;
    if (Object.keys(generated).length > 0) onGeneratedCardPositions(generated);
  }, [layout, onGeneratedCardPositions, pageItems, positions]);

  const positionOf = useCallback(
    (item: PipelineItem): { x: number; y: number } => positions.get(item.id) ?? { x: 20, y: 130 },
    [positions],
  );

  const anchorPointFor = useCallback((item: PipelineItem, side: CanvasSide, _offsetPercent: number): RoutePoint => {
    const pos = positionOf(item);
    const saved = layout[String(item.id)];
    const cardHeight = (renderMode === 'print' ? printCardMeasurements[item.id] : undefined)
      ?? cardHeights[item.id]
      ?? defaultCardHeight;
    const cardWidth = saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth;
    const offset = 0.5;
    if (side === 'top') return { x: pos.x + cardWidth * offset, y: pos.y };
    if (side === 'right') return { x: pos.x + cardWidth, y: pos.y + cardHeight * offset };
    if (side === 'left') return { x: pos.x, y: pos.y + cardHeight * offset };
    return { x: pos.x + cardWidth * offset, y: pos.y + cardHeight };
  }, [cardHeights, defaultCardHeight, defaultCardWidth, layout, positionOf, printCardMeasurements, renderMode]);

  const defaultAnchorSideOf = useCallback((item: PipelineItem): CanvasSide => {
    const pos = positionOf(item);
    const saved = layout[String(item.id)];
    const cardHeight = (renderMode === 'print' ? printCardMeasurements[item.id] : undefined)
      ?? cardHeights[item.id]
      ?? defaultCardHeight;
    const cardWidth = saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth;
    const target = linkedDots.get(item.id);
    if (!target) return 'bottom';
    // Attach from the edge facing the map. Cards in the left lane point from
    // their right edge, right-lane cards from their left edge, and so on.
    // This keeps every connector orthogonal and visually directed inward.
    const distances: Array<[CanvasSide, number]> = [
      ['left', pos.x],
      ['right', CANVAS_W - (pos.x + cardWidth)],
      ['top', pos.y],
      ['bottom', CANVAS_H - (pos.y + cardHeight)],
    ];
    const lane = distances.reduce((nearest, candidate) => candidate[1] < nearest[1] ? candidate : nearest)[0];
    if (lane === 'left') return 'right';
    if (lane === 'right') return 'left';
    if (lane === 'top') return 'bottom';
    return 'top';
  }, [cardHeights, defaultCardHeight, defaultCardWidth, layout, linkedDots, positionOf, printCardMeasurements, renderMode]);

  const resolvedAnchorSideOf = useCallback(
    (item: PipelineItem): CanvasSide => layout[String(item.id)]?.anchorSide ?? defaultAnchorSideOf(item),
    [defaultAnchorSideOf, layout],
  );

  const anchorOf = useCallback((item: PipelineItem) => {
    const side = resolvedAnchorSideOf(item);
    return anchorPointFor(item, side, 50);
  }, [anchorPointFor, layout, resolvedAnchorSideOf]);

  // The route identity changes only when the page, project membership, marker
  // geometry, or explicit placement generation changes. Card edits remain
  // local and do not invalidate unrelated connectors.
  const routingKey = useMemo(() => JSON.stringify({
    page: page.key,
    generation: routingGeneration,
    items: pageItems.map((item) => item.id),
    markers: positionedMarkers.map((marker) => ({
      x: marker.x,
      y: marker.y,
      radius: marker.radius,
      itemIds: marker.itemIds ?? [marker.item.id],
    })),
    // Print cards measure their natural height after mount. Include that
    // geometry in the print identity so readiness and connector anchors are
    // recalculated against the exact card footprint being captured.
    printCardGeometry: renderMode === 'print'
      ? pageItems.map((item) => ({
        id: item.id,
        width: layout[String(item.id)]?.width ?? defaultCardWidth / CANVAS_W * 100,
        height: renderMode === 'print' ? printCardMeasurements[item.id] ?? null : cardHeights[item.id] ?? null,
        anchorSide: layout[String(item.id)]?.anchorSide ?? null,
        anchorOffset: 50,
      }))
      : null,
  }), [cardHeights, defaultCardWidth, layout, page.key, pageItems, positionedMarkers, printCardMeasurements, renderMode, routingGeneration]);
  // Local anchor adjustments follow cards after generation without invoking
  // the worker or changing any other connector route.
  const [localRoutes, setLocalRoutes] = useState<Map<number, RoutePoint[]>>(new Map());
  // Local routes are previews only. Once the parent layout changes, rebuild
  // every connector from persisted card-side and shared marker-side geometry.
  // Keeping a stale full route here can make one member of a clustered marker
  // retain an old marker-to-junction tail while the other members use the new
  // shared tail.
  const midpointDragRef = useRef<{
    itemId: number;
    segmentIndex: number;
    pointerId: number;
    originalRoute: RoutePoint[];
    hadLocalRoute: boolean;
    sharedMarkerId?: number;
    originalSharedTail?: RoutePoint[];
  } | null>(null);

  // Bend coordinates are persisted in the layout. Clear only the transient
  // drag-preview layer after a layout/history update so undo and redo cannot
  // leave an old route overriding the restored layout.
  const printLayoutReady = renderMode !== 'print'
    || pageItems.every((item) => printCardMeasurements[item.id] != null);

  useEffect(() => {
    // Persisted layout is authoritative for both ordinary and clustered
    // connectors. This deliberately clears previews after every layout update
    // so marker-side bends are reconstructed once and shared by every card in
    // the cluster.
    setLocalRoutes(new Map());
    setForkOverrides(new Map());
    setSharedTailPreviews(new Map());
  }, [layout]);

  useEffect(() => {
    // Connectors are direct, deterministic card branches plus independently
    // rendered shared marker tails. Notify the export coordinator without
    // starting a routing job on tab entry.
    onRoutingComplete?.(page.key, {
      generation: routingGeneration,
      identity: routingKey,
      complete: printLayoutReady,
      routes: {},
      statuses: {},
      reasons: {},
    });
    if (PIPELINE_PDF_DEBUG && renderMode === 'print') {
      console.debug('[pipeline-pdf] print-readiness', {
        page: page.key,
        cardCount: pageItems.length,
        measuredCardCount: Object.keys(printCardMeasurements).length,
        measuredCardHeights: printCardMeasurements,
        routeIdentity: routingKey,
        complete: printLayoutReady,
        timestamp: new Date().toISOString(),
      });
    }

  }, [pageItems.length, printCardMeasurements, printLayoutReady, renderMode, routingKey, routingGeneration, onRoutingComplete, page.key]);

  const routedPaths = useMemo(() => {
    const base = new Map<number, RoutePoint[]>();
    if (!localRoutes.size) return base;
    const merged = new Map(base);
    localRoutes.forEach((route, itemId) => {
      const item = pageItems.find((candidate) => candidate.id === itemId);
      if (!item || route.length < 2) { merged.set(itemId, route); return; }
      // Re-anchor local edits against the persisted card geometry. This keeps
      // undo/redo and reloads attached without invoking global routing.
      const anchor = anchorOf(item);
      const next = route.map((point) => ({ ...point }));
      next[0] = anchor;
      const marker = positionedMarkers.find((candidate) => (candidate.itemIds ?? [candidate.item.id]).includes(itemId));
      const memberIds = marker?.itemIds ?? [itemId];
      // A clustered card route ends at the junction. Single-card routes still
      // end at the project marker.
      const movingFork = marker && memberIds.length > 1 ? forkOverrides.get(marker.item.id) : undefined;
      const savedFork = marker && memberIds.length > 1
        ? memberIds
          .map((id) => layout[String(id)])
          .find((saved) => saved?.forkX != null && saved?.forkY != null)
        : undefined;
      const fork = movingFork ?? (savedFork?.forkX != null && savedFork.forkY != null
        ? {
          x: savedFork.forkX / 100 * CANVAS_W,
          y: savedFork.forkY / 100 * CANVAS_H,
        }
        : undefined);
      const target = marker && memberIds.length > 1 ? fork : linkedDots.get(itemId);
      if (target) next[next.length - 1] = target;
      if (marker && memberIds.length > 1) {
        if (fork) {
          const forkIndex = next.findIndex((point) => Math.hypot(point.x - fork.x, point.y - fork.y) < 1);
          if (next[forkIndex]) {
            next[forkIndex] = fork;
          }
        }
      }
      // Explicit bend points are intentionally retained even when the user
      // adds one to a horizontal/vertical line.  The old collinear cleanup
      // would immediately remove that new point, making the line appear to
      // reset and leaving no draggable circle.
      merged.set(itemId, dedupeRoute(next, true));
    });
    return merged;
  }, [anchorOf, forkOverrides, layout, linkedDots, localRoutes, pageItems, positionedMarkers]);

  // Connectors are intentionally simple after card placement: the card is
  // linked directly to its marker. Clustered markers use one shared fork
  // junction, so every member branches from the same visible point.
  const forkForMarker = useCallback((marker: MapMarker, memberIds: number[]): RoutePoint => {
    if (memberIds.length <= 1) return { x: marker.x, y: marker.y };
    const override = forkOverrides.get(marker.item.id);
    if (override) return override;
    // Fork coordinates are stored on each member card for backwards
    // compatibility with the existing layout schema.  Read the first valid
    // value so the junction remains shared when cards from the same marker
    // are selected or moved independently.
    const savedFork = memberIds
      .map((id) => layout[String(id)])
      .find((saved) => saved?.forkX != null && saved?.forkY != null);
    if (savedFork?.forkX != null && savedFork.forkY != null) {
      return {
        x: savedFork.forkX / 100 * CANVAS_W,
        y: savedFork.forkY / 100 * CANVAS_H,
      };
    }
    const anchors = memberIds
      .map((id) => pageItems.find((item) => item.id === id))
      .filter((item): item is PipelineItem => Boolean(item))
      .map((item) => anchorOf(item));
    if (!anchors.length) return { x: marker.x, y: marker.y - 14 };
    const average = anchors.reduce(
      (sum, point) => ({ x: sum.x + point.x / anchors.length, y: sum.y + point.y / anchors.length }),
      { x: 0, y: 0 },
    );
    // Keep the junction visible even when the member anchors surround the
    // marker symmetrically and their average lands on its centre.
    const averageDirection = { x: average.x - marker.x, y: average.y - marker.y };
    const fallbackAnchor = anchors
      .slice()
      .sort((a, b) => Math.hypot(b.x - marker.x, b.y - marker.y) - Math.hypot(a.x - marker.x, a.y - marker.y))[0];
    const fallbackDirection = fallbackAnchor
      ? { x: fallbackAnchor.x - marker.x, y: fallbackAnchor.y - marker.y }
      : { x: 0, y: -1 };
    const direction = Math.hypot(averageDirection.x, averageDirection.y) > 1
      ? averageDirection
      : (Math.hypot(fallbackDirection.x, fallbackDirection.y) > 1 ? fallbackDirection : { x: 0, y: -1 });
    const distance = Math.hypot(direction.x, direction.y) || 1;
    const length = Math.min(18, distance * 0.25);
    return { x: marker.x + direction.x / distance * length, y: marker.y + direction.y / distance * length };
  }, [anchorOf, forkOverrides, layout, pageItems]);

  const canvasPointFromPointer = (event: React.PointerEvent<SVGCircleElement>): RoutePoint | null => {
    const canvas = event.currentTarget.closest('[data-pipeline-page]') as HTMLElement | null;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: Math.max(0, Math.min(CANVAS_W, (event.clientX - rect.left) / rect.width * CANVAS_W)),
      y: Math.max(0, Math.min(CANVAS_H, (event.clientY - rect.top) / rect.height * CANVAS_H)),
    };
  };

  const markerDragRef = useRef<{ layoutKey: string; origin: RoutePoint } | null>(null);
  const handleMarkerPointerDown = (event: React.PointerEvent<SVGCircleElement>, marker: MapMarker) => {
    if (!editable || fixed || !onMarkerMove) return;
    markerDragRef.current = { layoutKey: marker.layoutKey, origin: { x: marker.x, y: marker.y } };
    autoPan.start(event.clientX, event.clientY);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };
  const handleMarkerPointerMove = (event: React.PointerEvent<SVGCircleElement>) => {
    const drag = markerDragRef.current;
    if (!drag || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    autoPan.move(event.clientX, event.clientY);
    const point = canvasPointFromPointer(event);
    if (!point) return;
    setMarkerDragOverrides((current) => new Map(current).set(drag.layoutKey, point));
    event.preventDefault();
    event.stopPropagation();
  };
  const handleMarkerPointerUp = (event: React.PointerEvent<SVGCircleElement>) => {
    const drag = markerDragRef.current;
    if (!drag) return;
    autoPan.stop();
    const point = canvasPointFromPointer(event) ?? drag.origin;
    onMarkerMove?.(drag.layoutKey, point.x / CANVAS_W * 100, point.y / CANVAS_H * 100);
    setMarkerDragOverrides((current) => {
      if (!current.has(drag.layoutKey)) return current;
      const next = new Map(current);
      next.delete(drag.layoutKey);
      return next;
    });
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    markerDragRef.current = null;
    event.preventDefault();
    event.stopPropagation();
  };
  const handleMarkerPointerCancel = (event: React.PointerEvent<SVGCircleElement>) => {
    const drag = markerDragRef.current;
    if (!drag) return;
    autoPan.stop();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    markerDragRef.current = null;
    setMarkerDragOverrides((current) => {
      if (!current.has(drag.layoutKey)) return current;
      const next = new Map(current);
      next.delete(drag.layoutKey);
      return next;
    });
    event.preventDefault();
    event.stopPropagation();
  };

  const forkIsSelected = (memberIds: number[]) =>
    editable && !fixed && memberIds.some((id) => selectedCardIds.has(id));

  const handleForkPointerDown = (
    event: React.PointerEvent<SVGCircleElement>,
    marker: MapMarker,
    memberIds: number[],
  ) => {
    if (!editable || fixed) return;
    const origin = forkForMarker(marker, memberIds);
    forkDragRef.current = {
      markerId: marker.item.id,
      memberIds,
      startX: event.clientX,
      startY: event.clientY,
      origin,
      originalLocalRoutes: new Map(memberIds.flatMap((id) => {
        const route = localRoutes.get(id);
        return route ? [[id, route.map((point) => ({ ...point }))] as [number, RoutePoint[]]] : [];
      })),
    };
    autoPan.start(event.clientX, event.clientY);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleForkPointerMove = (event: React.PointerEvent<SVGCircleElement>) => {
    const drag = forkDragRef.current;
    if (!drag || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    autoPan.move(event.clientX, event.clientY);
    const point = canvasPointFromPointer(event);
    if (!point) return;
    setForkOverrides((current) => new Map(current).set(drag.markerId, point));
    // Existing locally edited routes may have their own marker-side tail.
    // Replace only that tail with one shared marker-to-junction route so all
    // cards under this marker remain visually joined while the fork moves.
    const marker = positionedMarkers.find((candidate) => candidate.item.id === drag.markerId);
    if (marker) {
      const sharedTail = sharedMarkerTailFor(marker, drag.memberIds, point);
      setSharedTailPreviews((current) => new Map(current).set(
        drag.markerId,
        dedupeRoute(sharedTail, true),
      ));
      setLocalRoutes((current) => {
        const next = new Map(current);
        drag.memberIds.forEach((memberId) => {
          const member = pageItems.find((item) => item.id === memberId);
          if (!member) return;
          const memberLayout = layout[String(memberId)] ?? {};
          const anchor = anchorOf(member);
          const branchStyle = memberLayout.lineStyle ?? defaultLineStyle(anchor, point);
          const branchCount = bendCountForStyle(branchStyle);
          const anchorSide = resolvedAnchorSideOf(member);
          const horizontalFirst = anchorSide === 'left' || anchorSide === 'right'
            ? false
            : anchorSide === 'top' || anchorSide === 'bottom'
              ? true
              : undefined;
          // Junction movement is an explicit branch recalculation. Rebuild
          // the card-side route from the card anchor to the live junction on
          // every pointer move instead of moving the old fork point inside a
          // stale route with obsolete bends.
          const branch = recalculatedCardBranch(anchor, point, branchCount, horizontalFirst);
          next.set(memberId, branch.route);
        });
        return next;
      });
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handleForkPointerUp = (event: React.PointerEvent<SVGCircleElement>) => {
    const drag = forkDragRef.current;
    if (!drag) return;
    autoPan.stop();
    const point = canvasPointFromPointer(event) ?? forkOverrides.get(drag.markerId) ?? drag.origin;
    // Persist the shared fork and the newly recalculated card branch on every
    // member. Without the branch bends, the layout effect would clear the
    // live preview and customRouteFor would rebuild from bends belonging to
    // the old junction position.
    drag.memberIds.forEach((id) => {
      const member = pageItems.find((item) => item.id === id);
      if (!member) return;
      const saved = layout[String(id)] ?? {};
      const anchor = anchorOf(member);
      const branchStyle = saved.lineStyle ?? defaultLineStyle(anchor, point);
      const branchCount = bendCountForStyle(branchStyle);
      const anchorSide = resolvedAnchorSideOf(member);
      const horizontalFirst = anchorSide === 'left' || anchorSide === 'right'
        ? false
        : anchorSide === 'top' || anchorSide === 'bottom'
          ? true
          : undefined;
      const branch = recalculatedCardBranch(anchor, point, branchCount, horizontalFirst);
      const bendPoints = branch.bends.slice(0, 7);
      const bendPatch: Partial<CardPosition> = {
        bendX: bendPoints[0] ? bendPoints[0].x / CANVAS_W * 100 : undefined,
        bendY: bendPoints[0] ? bendPoints[0].y / CANVAS_H * 100 : undefined,
        bend2X: bendPoints[1] ? bendPoints[1].x / CANVAS_W * 100 : undefined,
        bend2Y: bendPoints[1] ? bendPoints[1].y / CANVAS_H * 100 : undefined,
        bend3X: bendPoints[2] ? bendPoints[2].x / CANVAS_W * 100 : undefined,
        bend3Y: bendPoints[2] ? bendPoints[2].y / CANVAS_H * 100 : undefined,
        bend4X: bendPoints[3] ? bendPoints[3].x / CANVAS_W * 100 : undefined,
        bend4Y: bendPoints[3] ? bendPoints[3].y / CANVAS_H * 100 : undefined,
        bend5X: bendPoints[4] ? bendPoints[4].x / CANVAS_W * 100 : undefined,
        bend5Y: bendPoints[4] ? bendPoints[4].y / CANVAS_H * 100 : undefined,
        bend6X: bendPoints[5] ? bendPoints[5].x / CANVAS_W * 100 : undefined,
        bend6Y: bendPoints[5] ? bendPoints[5].y / CANVAS_H * 100 : undefined,
        bend7X: bendPoints[6] ? bendPoints[6].x / CANVAS_W * 100 : undefined,
        bend7Y: bendPoints[6] ? bendPoints[6].y / CANVAS_H * 100 : undefined,
      };
      onLineLayoutChange?.(id, {
        forkX: point.x / CANVAS_W * 100,
        forkY: point.y / CANVAS_H * 100,
        lineStyle: branchStyle,
        ...bendPatch,
      });
    });
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    forkDragRef.current = null;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleForkPointerCancel = (event: React.PointerEvent<SVGCircleElement>) => {
    const drag = forkDragRef.current;
    if (!drag) return;
    autoPan.stop();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    forkDragRef.current = null;
    setLocalRoutes((current) => {
      const next = new Map(current);
      drag.memberIds.forEach((memberId) => next.delete(memberId));
      drag.originalLocalRoutes.forEach((route, memberId) => next.set(memberId, route));
      return next;
    });
    setSharedTailPreviews((current) => {
      if (!current.has(drag.markerId)) return current;
      const next = new Map(current);
      next.delete(drag.markerId);
      return next;
    });
    setForkOverrides((current) => {
      if (!current.has(drag.markerId)) return current;
      const next = new Map(current);
      next.delete(drag.markerId);
      return next;
    });
  };

  /**
   * Build the one canonical marker-side tail for a clustered marker. Every
   * member card consumes this exact point sequence; card-specific branches are
   * rendered separately and meet it at the shared fork.
   */
  const sharedMarkerTailFor = useCallback((marker: MapMarker, memberIds: number[], forkOverride?: RoutePoint): RoutePoint[] => {
    const fork = forkOverride ?? forkForMarker(marker, memberIds);
    const markerLayout = layout[marker.layoutKey] ?? {};
    const markerBendKeys: Array<[keyof CardPosition, keyof CardPosition]> = [
      ['markerBendX', 'markerBendY'], ['markerBend2X', 'markerBend2Y'], ['markerBend3X', 'markerBend3Y'],
      ['markerBend4X', 'markerBend4Y'], ['markerBend5X', 'markerBend5Y'], ['markerBend6X', 'markerBend6Y'],
      ['markerBend7X', 'markerBend7Y'],
    ];
    const hasSavedMarkerBend = markerBendKeys.some(([xKey, yKey]) => markerLayout[xKey] != null && markerLayout[yKey] != null);
    const savedMarkerBendCount = markerBendKeys.reduce((count, [xKey, yKey]) => (
      markerLayout[xKey] != null && markerLayout[yKey] != null ? count + 1 : count
    ), 0);
    const markerRouteEdited = markerLayout.markerRouteEdited === true
      || markerLayout.markerLineStyle != null
      || hasSavedMarkerBend;
    const markerCount = markerRouteEdited
      ? Math.max(bendCountForStyle(markerLayout.markerLineStyle), savedMarkerBendCount)
      : 0;
    const generated = generatedOrthogonalBends(fork, marker, markerCount);
    const bends = Array.from({ length: markerCount }, (_, index) => {
      const [xKey, yKey] = markerBendKeys[index];
      const x = markerLayout[xKey] as number | undefined;
      const y = markerLayout[yKey] as number | undefined;
      return markerRouteEdited && x != null && y != null
        ? { x: x / 100 * CANVAS_W, y: y / 100 * CANVAS_H }
        : generated[index];
    }).filter((point): point is RoutePoint => Boolean(point));
    return [fork, ...bends, { x: marker.x, y: marker.y }];
  }, [forkForMarker, layout]);

  const customRouteFor = useCallback((
    anchor: RoutePoint,
    marker: MapMarker,
    memberIds: number[],
    saved: CardPosition,
    anchorSide?: CanvasSide,
  ): RoutePoint[] => {
    const fork = forkForMarker(marker, memberIds);
    const bendKeys: Array<[keyof CardPosition, keyof CardPosition]> = [
      ['bendX', 'bendY'], ['bend2X', 'bend2Y'], ['bend3X', 'bend3Y'], ['bend4X', 'bend4Y'],
      ['bend5X', 'bend5Y'], ['bend6X', 'bend6Y'], ['bend7X', 'bend7Y'],
    ];
    // User bends are placed before the shared fork. The fork itself remains a
    // generated junction and is always retained for clustered markers.
    const bendTarget = memberIds.length > 1 ? fork : { x: marker.x, y: marker.y };
    // Generated routes use the same style decision as the worker and print
    // renderer. Explicit saved styles always remain authoritative.
    const count = bendCountForStyle(saved.lineStyle ?? defaultLineStyle(anchor, bendTarget));
    // For a left/right card the final segment must be horizontal into the
    // card; for a top/bottom card it must be vertical. This makes the route
    // leave the marker in the natural direction instead of making a needless
    // turn around it.
    const horizontalFirst = anchorSide === 'left' || anchorSide === 'right'
      ? false
      : anchorSide === 'top' || anchorSide === 'bottom'
        ? true
        : undefined;
    const generatedBends = generatedOrthogonalBends(anchor, bendTarget, count, horizontalFirst);
    const bends = Array.from({ length: count }, (_, index) => {
      const [xKey, yKey] = bendKeys[index];
      const savedX = saved[xKey] as number | undefined;
      const savedY = saved[yKey] as number | undefined;
      if (savedX != null && savedY != null) return { x: savedX / 100 * CANVAS_W, y: savedY / 100 * CANVAS_H };
      return generatedBends[index];
    });
    const cardBranch = dedupeRoute([
      anchor,
      ...bends,
      memberIds.length > 1 ? fork : { x: marker.x, y: marker.y },
    ], true);
    if (memberIds.length <= 1) return cardBranch;

    // Multi-project cards own only their card-to-junction branch. The
    // marker-to-junction route is rendered and edited separately as one
    // shared connector for the marker group.
    return cardBranch;
  }, [forkForMarker]);

  const directConnectorRoutes = useMemo(() => {
    const routes = new Map<number, RoutePoint[]>();
    positionedMarkers.forEach((marker) => {
      const memberIds = (marker.itemIds ?? [marker.item.id]).filter((id) => pageItems.some((item) => item.id === id));
      if (!memberIds.length) return;
      memberIds.forEach((id) => {
        const item = pageItems.find((candidate) => candidate.id === id);
        if (!item) return;
        routes.set(id, customRouteFor(anchorOf(item), marker, memberIds, layout[String(id)] ?? {}, resolvedAnchorSideOf(item)));
      });
    });
    return routes;
  }, [anchorOf, customRouteFor, layout, pageItems, positionedMarkers, resolvedAnchorSideOf]);
  const connectorItems = renderConnectors
    ? pageItems.filter((item) => linkedDots.has(item.id))
    : [];
  const displayedConnectorRoutes = useMemo(() => {
    const routes = new Map(directConnectorRoutes);
    localRoutes.forEach((_route, itemId) => {
      const edited = routedPaths.get(itemId);
      if (edited) routes.set(itemId, edited);
    });
    return routes;
  }, [directConnectorRoutes, localRoutes, routedPaths]);

  const connectorPartsFor = useCallback((itemId: number, route: RoutePoint[]) => {
    const marker = positionedMarkers.find((candidate) => (candidate.itemIds ?? [candidate.item.id]).includes(itemId));
    const memberIds = marker?.itemIds ?? [itemId];
    if (!marker || memberIds.length <= 1) {
      return { branch: route, sharedTail: null as RoutePoint[] | null, markerId: null as number | null };
    }
    const fork = forkForMarker(marker, memberIds);
    const canonicalTail = dedupeRoute(sharedMarkerTailFor(marker, memberIds), true);
    const forkIndex = route.findIndex((point) => Math.hypot(point.x - fork.x, point.y - fork.y) < 1);
    // Card routes are now stored as branch-only routes. They must end at the
    // current fork; never treat a card route as if it also owned the marker
    // tail.
    const branch = forkIndex >= 0
      ? route.slice(0, forkIndex + 1)
      : route;
    return {
      branch,
      sharedTail: sharedTailPreviews.get(marker.item.id)
        ?? canonicalTail,
      markerId: marker.item.id,
    };
  }, [forkForMarker, positionedMarkers, sharedMarkerTailFor, sharedTailPreviews]);

  useEffect(() => {
    if (!PIPELINE_PDF_DEBUG || renderMode !== 'print') return;
    const geometry = pageItems.map((item) => {
      const position = positionOf(item);
      const saved = layout[String(item.id)];
      const width = saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth;
      const height = printCardMeasurements[item.id] ?? cardHeights[item.id] ?? defaultCardHeight;
      const route = displayedConnectorRoutes.get(item.id);
      return {
        itemId: item.id,
        bounds: { x: position.x, y: position.y, width, height },
        anchor: anchorOf(item),
        routeStart: route?.[0] ?? null,
        routeEnd: route?.[route.length - 1] ?? null,
        routePoints: route?.length ?? 0,
      };
    });
    console.debug('[pipeline-pdf] print-geometry', {
      page: page.key,
      routeIdentity: routingKey,
      geometry,
      timestamp: new Date().toISOString(),
    });
  }, [anchorOf, cardHeights, defaultCardHeight, defaultCardWidth, displayedConnectorRoutes, layout, page.key, pageItems, positionOf, printCardMeasurements, renderMode, routingKey]);

  // Card geometry edits intentionally do not invalidate the generated route
  // set. Connectors remain the snapshot produced during generation/reflow.
  const followCardAnchor = (itemId: number, geometry: { x: number; y: number; w: number; h: number }) => {
    const item = pageItems.find((candidate) => candidate.id === itemId);
    if (!item) return;
    const saved = layout[String(itemId)];
    const side = resolvedAnchorSideOf(item);
    const offset = 0.5;
    const anchor = side === 'top'
      ? { x: geometry.x + geometry.w * offset, y: geometry.y }
      : side === 'right'
        ? { x: geometry.x + geometry.w, y: geometry.y + geometry.h * offset }
        : side === 'left'
          ? { x: geometry.x, y: geometry.y + geometry.h * offset }
          : { x: geometry.x + geometry.w * offset, y: geometry.y + geometry.h };
    const memberMarker = positionedMarkers.find((marker) => (marker.itemIds ?? [marker.item.id]).includes(itemId));
    const memberIds = memberMarker?.itemIds ?? [itemId];
    const target = memberMarker && memberIds.length > 1
      ? forkForMarker(memberMarker, memberIds)
      : linkedDots.get(itemId);
    if (!target) return;
    const hasCustomBends = bendCountForStyle(saved?.lineStyle) > 0 || localRoutes.has(itemId);
    if (!hasCustomBends) {
      // Generated straight routes are derived from the current card geometry,
      // so no transient route is needed and clicks/moves cannot detach them.
      setLocalRoutes((previous) => {
        if (!previous.has(itemId)) return previous;
        const next = new Map(previous);
        next.delete(itemId);
        return next;
      });
      return;
    }
    // Preserve user-created bends while moving the card. Only the card-side
    // endpoint changes; the marker endpoint and all bend points stay fixed.
    const current = displayedConnectorRoutes.get(itemId)
      ?? customRouteFor(anchor, memberMarker ?? {
        item,
        number: 0,
        x: target.x,
        y: target.y,
        radius: 0,
        layoutKey: markerLayoutKey(memberIds),
        itemIds: memberIds,
      }, memberIds, saved ?? {});
    const next = current.map((point) => ({ ...point }));
    next[0] = anchor;
    next[next.length - 1] = target;
    setLocalRoutes((previous) => new Map(previous).set(itemId, next));
  };

  const moveCardSafely = (itemId: number, xPct: number, yPct: number, final: boolean) => {
    const target = { x: xPct / 100 * CANVAS_W, y: yPct / 100 * CANVAS_H };
    const isMultiDrag = multiSelectMode && selectedCardIds.has(itemId) && selectedCardIds.size > 1;
    const groupIds = isMultiDrag
      ? Array.from(selectedCardIds)
      : [itemId];
    if (!isMultiDrag) {
      multiDragRef.current = null;
    } else if (!multiDragRef.current || multiDragRef.current.draggedId !== itemId) {
      const origins = new Map<number, RoutePoint>();
      groupIds.forEach((id) => {
        const item = pageItems.find((candidate) => candidate.id === id);
        if (item) origins.set(id, positionOf(item));
      });
      const originDragged = origins.get(itemId) ?? positionOf(pageItems.find((item) => item.id === itemId)!);
      multiDragRef.current = { draggedId: itemId, originDragged, origins };
    }
    const origin = isMultiDrag
      ? (multiDragRef.current?.originDragged ?? positionOf(pageItems.find((item) => item.id === itemId)!))
      : positionOf(pageItems.find((item) => item.id === itemId)!);
    const dx = target.x - origin.x;
    const dy = target.y - origin.y;
    const destinations = groupIds.map((id) => {
      const saved = layout[String(id)];
      const base = isMultiDrag
        ? multiDragRef.current?.origins.get(id)
        : undefined;
      const pos = id === itemId
        ? target
        : base
          ? { x: base.x + dx, y: base.y + dy }
          : {
            x: positionOf(pageItems.find((item) => item.id === id)!).x + dx,
            y: positionOf(pageItems.find((item) => item.id === id)!).y + dy,
          };
      return {
        id,
        ...pos,
        w: saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth,
        h: saved?.height != null ? saved.height / 100 * CANVAS_H : defaultCardHeight,
      };
    });
    if (final) {
      setCardAlignmentGuides(null);
    } else {
      const movingIds = new Set(groupIds);
      const stationary = pageItems
        .filter((item) => !movingIds.has(item.id))
        .map((item) => {
          const saved = layout[String(item.id)];
          const position = positionOf(item);
          return {
            x: position.x,
            y: position.y,
            w: saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth,
            h: saved?.height != null ? saved.height / 100 * CANVAS_H : defaultCardHeight,
          };
        });
      let bestVerticalCoordinate: number | undefined;
      let bestHorizontalCoordinate: number | undefined;
      let bestVerticalDistance = Number.POSITIVE_INFINITY;
      let bestHorizontalDistance = Number.POSITIVE_INFINITY;
      destinations.forEach((moving) => {
        const movingXs = [moving.x, moving.x + moving.w / 2, moving.x + moving.w];
        const movingYs = [moving.y, moving.y + moving.h / 2, moving.y + moving.h];
        stationary.forEach((other) => {
          const otherXs = [other.x, other.x + other.w / 2, other.x + other.w];
          const otherYs = [other.y, other.y + other.h / 2, other.y + other.h];
          movingXs.forEach((movingX) => otherXs.forEach((otherX) => {
            const distance = Math.abs(movingX - otherX);
            if (distance <= CARD_ALIGNMENT_GUIDE_TOLERANCE
              && distance < bestVerticalDistance) {
              bestVerticalCoordinate = otherX;
              bestVerticalDistance = distance;
            }
          }));
          movingYs.forEach((movingY) => otherYs.forEach((otherY) => {
            const distance = Math.abs(movingY - otherY);
            if (distance <= CARD_ALIGNMENT_GUIDE_TOLERANCE
              && distance < bestHorizontalDistance) {
              bestHorizontalCoordinate = otherY;
              bestHorizontalDistance = distance;
            }
          }));
        });
      });
      setCardAlignmentGuides(bestVerticalCoordinate != null || bestHorizontalCoordinate != null ? {
        ...(bestVerticalCoordinate != null ? { vertical: bestVerticalCoordinate } : {}),
        ...(bestHorizontalCoordinate != null ? { horizontal: bestHorizontalCoordinate } : {}),
      } : null);
    }
    // Placement collision rules apply only to generated slots. Once the deck
    // exists, card movement is intentionally free-form so users can arrange
    // the template without the generator fighting their edits.
    destinations.forEach((destination) => {
      followCardAnchor(destination.id, destination);
      const x = destination.x / CANVAS_W * 100;
      const y = destination.y / CANVAS_H * 100;
      if (final) {
        onCardDrop?.(destination.id, x, y);
      }
      else onCardMove?.(destination.id, x, y);
    });
    if (final) multiDragRef.current = null;
  };

  const cardGeometryFor = (itemId: number) => {
    const item = pageItems.find((candidate) => candidate.id === itemId);
    if (!item) return null;
    const saved = layout[String(itemId)];
    const position = positionOf(item);
    return {
      x: position.x,
      y: position.y,
      w: saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth,
      h: saved?.height != null ? saved.height / 100 * CANVAS_H : defaultCardHeight,
    };
  };

  const beginCardResize = (itemId: number) => {
    setCardAlignmentGuides(null);
    const isMultiResize = multiSelectMode && selectedCardIds.has(itemId) && selectedCardIds.size > 1;
    if (!isMultiResize) {
      multiResizeRef.current = null;
      return;
    }
    const geometries = new Map<number, { x: number; y: number; w: number; h: number }>();
    selectedCardIds.forEach((id) => {
      const geometry = cardGeometryFor(id);
      if (geometry) geometries.set(id, geometry);
    });
    multiResizeRef.current = { resizedId: itemId, geometries };
  };

  const endCardResize = () => {
    multiResizeRef.current = null;
  };

  const resizeCardSafely = (itemId: number, widthPct: number, heightPct: number) => {
    const activeGeometry = cardGeometryFor(itemId);
    if (!activeGeometry) return;
    const isMultiResize = multiSelectMode && selectedCardIds.has(itemId) && selectedCardIds.size > 1;
    if (isMultiResize) {
      // DeckCard reports the proposed size of the card whose handle is being
      // dragged. Apply that same width/height scale to every selected card,
      // using the snapshot captured at pointer-down so batched React updates
      // cannot make the group drift or resize incrementally.
      const snapshot = multiResizeRef.current?.resizedId === itemId
        ? multiResizeRef.current
        : (() => {
          const geometries = new Map<number, { x: number; y: number; w: number; h: number }>();
          selectedCardIds.forEach((id) => {
            const geometry = cardGeometryFor(id);
            if (geometry) geometries.set(id, geometry);
          });
          const next = { resizedId: itemId, geometries };
          multiResizeRef.current = next;
          return next;
        })();
      const originalActive = snapshot.geometries.get(itemId) ?? activeGeometry;
      const widthScale = (widthPct / 100 * CANVAS_W) / Math.max(1, originalActive.w);
      const heightScale = (heightPct / 100 * CANVAS_H) / Math.max(1, originalActive.h);
      const dominantScale = Math.abs(widthScale - 1) >= Math.abs(heightScale - 1) ? widthScale : heightScale;
      snapshot.geometries.forEach((original, id) => {
        const saved = layout[String(id)];
        const scale = saved?.aspectLocked ? dominantScale : undefined;
        const nextWidth = Math.min(
          Math.max(120, original.w * (scale ?? widthScale)),
          Math.max(120, CANVAS_W - original.x),
        );
        const nextHeight = Math.min(
          Math.max(70, original.h * (scale ?? heightScale)),
          Math.max(70, CANVAS_H - original.y),
        );
        const candidate = { x: original.x, y: original.y, w: nextWidth, h: nextHeight };
        followCardAnchor(id, candidate);
        onCardResize?.(
          id,
          nextWidth / CANVAS_W * 100,
          nextHeight / CANVAS_H * 100,
          original.x / CANVAS_W * 100,
          original.y / CANVAS_H * 100,
        );
      });
      return;
    }
    const pos = positions.get(itemId);
    if (!pos) return;
    const candidate = { x: pos.x, y: pos.y, w: widthPct / 100 * CANVAS_W, h: heightPct / 100 * CANVAS_H };
    // Resizing is intentionally free-form after generation. The initial
    // allocator protects furniture and markers; subsequent user edits do not
    // reapply those collision constraints.
    // Persist the current position together with a resize. This promotes the
    // generated slot to explicit user geometry, so a resize cannot trigger a
    // fresh allocator pass that moves the card on the next render.
    followCardAnchor(itemId, candidate);
    onCardResize?.(
      itemId,
      widthPct,
      heightPct,
      pos.x / CANVAS_W * 100,
      pos.y / CANVAS_H * 100,
    );
  };

  const selectedLineItem = lineMenu ? pageItems.find((item) => item.id === lineMenu.itemId) : undefined;
  const selectedLineLayout = selectedLineItem ? layout[String(selectedLineItem.id)] : undefined;
  const lineStyleForBendCount = (count: number): NonNullable<CardPosition['lineStyle']> =>
    count <= 0 ? 'straight' : count === 1 ? 'one-bend' : count === 2 ? 'two-bend' : count === 3 ? 'three-bend' : count === 4 ? 'four-bend' : count === 5 ? 'five-bend' : count === 6 ? 'six-bend' : 'seven-bend';
  const bendLayoutPatch = (route: RoutePoint[] | null, count: number): Partial<CardPosition> => {
    const points = route?.slice(1, 1 + Math.min(7, count)) ?? [];
    const value = (point: RoutePoint | undefined, axis: 'x' | 'y') => point ? point[axis] / (axis === 'x' ? CANVAS_W : CANVAS_H) * 100 : undefined;
    return {
      bendX: value(points[0], 'x'), bendY: value(points[0], 'y'),
      bend2X: value(points[1], 'x'), bend2Y: value(points[1], 'y'),
      bend3X: value(points[2], 'x'), bend3Y: value(points[2], 'y'),
      bend4X: value(points[3], 'x'), bend4Y: value(points[3], 'y'),
      bend5X: value(points[4], 'x'), bend5Y: value(points[4], 'y'),
      bend6X: value(points[5], 'x'), bend6Y: value(points[5], 'y'),
      bend7X: value(points[6], 'x'), bend7Y: value(points[6], 'y'),
    };
  };
  // Changing the card side is a topology change, not a bend edit. Rebuild
  // the card-to-marker portion from the new centred anchor and use the fewest
  // orthogonal bends needed to reach the existing joint. Reusing the old
  // card-side bends here is what previously produced diagonal joins and long
  // carry-over segments from the former side.
  const recalculateRouteForSide = (itemId: number, side: CanvasSide): RoutePoint[] | null => {
    const item = pageItems.find((candidate) => candidate.id === itemId);
    const marker = positionedMarkers.find((candidate) => (candidate.itemIds ?? [candidate.item.id]).includes(itemId));
    const target = linkedDots.get(itemId);
    if (!item || !target) return null;
    const memberIds = marker?.itemIds ?? [itemId];
    const anchor = anchorPointFor(item, side, 50);
    const fork = marker && memberIds.length > 1 ? forkForMarker(marker, memberIds) : undefined;
    const cardTarget = fork ?? target;
    const cardBendCount = defaultLineStyle(anchor, cardTarget) === 'straight' ? 0 : 1;
    const cardBends = generatedOrthogonalBends(
      anchor,
      cardTarget,
      cardBendCount,
      side === 'left' || side === 'right' ? false : true,
    );
    if (!marker || memberIds.length <= 1) {
      return dedupeRoute([anchor, ...cardBends, target]);
    }

    // A clustered card route ends at the junction. The shared marker tail is
    // rendered and edited independently, so changing the card side must not
    // copy marker-side bends into this route.
    return dedupeRoute([anchor, ...cardBends, fork ?? target]);
  };
  const selectedAnchorSide = selectedLineItem ? resolvedAnchorSideOf(selectedLineItem) : 'bottom';

  const midpointForSegment = (a: RoutePoint, b: RoutePoint): RoutePoint => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  });
  const uniqueRouteSegments = (route: RoutePoint[], protectedBreaks?: Set<number>) => {
    const seen = new Set<string>();
    return route.slice(0, -1).reduce<Array<{ start: RoutePoint; end: RoutePoint; segmentIndex: number; endIndex: number }>>((segments, point, index) => {
      const next = route[index + 1];
      if (Math.abs(point.x - next.x) < 0.5 && Math.abs(point.y - next.y) < 0.5) return segments;
      const previous = segments[segments.length - 1];
      const previousHorizontal = previous ? Math.abs(previous.start.y - previous.end.y) < 0.5 : false;
      const horizontal = Math.abs(point.y - next.y) < 0.5;
      const continuesStraight = previous
        && previous.endIndex === index
        && previousHorizontal === horizontal
        && !protectedBreaks?.has(index)
        && (horizontal ? Math.abs(previous.end.y - point.y) < 0.5 : Math.abs(previous.end.x - point.x) < 0.5);
      if (continuesStraight) {
        previous.end = next;
        previous.endIndex = index + 1;
        return segments;
      }
      const first = `${Math.round(point.x * 2) / 2},${Math.round(point.y * 2) / 2}`;
      const second = `${Math.round(next.x * 2) / 2},${Math.round(next.y * 2) / 2}`;
      const key = [first, second].sort().join('|');
      if (seen.has(key)) return segments;
      seen.add(key);
      segments.push({ start: point, end: next, segmentIndex: index, endIndex: index + 1 });
      return segments;
    }, []);
  };
  const routeWithMidpointBend = (
    route: RoutePoint[],
    segmentIndex: number,
    pointer: RoutePoint,
    segmentEndIndex = segmentIndex + 1,
    preserveStart = false,
    preserveSharedBoundary = false,
  ): RoutePoint[] => {
    const a = route[segmentIndex];
    const b = route[segmentEndIndex];
    if (!a || !b) return route;
    const horizontal = Math.abs(a.y - b.y) < 0.5;
    const offset = horizontal ? pointer.y : pointer.x;
    const build = (nextOffset: number) => {
      const bends = horizontal
        ? [{ x: a.x, y: nextOffset }, { x: b.x, y: nextOffset }]
        : [{ x: nextOffset, y: a.y }, { x: nextOffset, y: b.y }];
      return [...route.slice(0, segmentIndex + 1), ...bends, ...route.slice(segmentEndIndex)];
    };
    // An interior segment already has two adjacent corners. Move those
    // corners together; endpoint-adjacent segments need two new corners.
    if (!preserveStart && segmentIndex > 0 && segmentEndIndex < route.length - 1) {
      const next = route.map((point) => ({ ...point }));
      for (let index = segmentIndex; index <= segmentEndIndex; index += 1) {
        if (horizontal) next[index].y = offset;
        else next[index].x = offset;
      }
      return dedupeRoute(next, preserveSharedBoundary);
    }
    // Editing is intentionally unrestricted: cards, legends, and other
    // furniture do not trigger route recalculation or offset snapping.
    // Remove the superseded collinear points from the original route. Keeping
    // them in the polyline is what creates the small tail/stub after a pill
    // is dragged: the new detour is drawn, but the old intermediate points
    // still contribute another piece of line.
    return dedupeRoute(build(offset).map((point) => ({ ...point })), preserveSharedBoundary);
  };
  const midpointPatch = (route: RoutePoint[], count: number, prefix: 'bend' | 'markerBend'): Partial<CardPosition> => {
    const keys = prefix === 'bend'
      ? [['bendX', 'bendY'], ['bend2X', 'bend2Y'], ['bend3X', 'bend3Y'], ['bend4X', 'bend4Y'], ['bend5X', 'bend5Y'], ['bend6X', 'bend6Y'], ['bend7X', 'bend7Y']]
      : [['markerBendX', 'markerBendY'], ['markerBend2X', 'markerBend2Y'], ['markerBend3X', 'markerBend3Y'], ['markerBend4X', 'markerBend4Y'], ['markerBend5X', 'markerBend5Y'], ['markerBend6X', 'markerBend6Y'], ['markerBend7X', 'markerBend7Y']];
    const points = route.slice(1, 1 + Math.min(7, count));
    const patch: Partial<CardPosition> = {};
    keys.forEach(([xKey, yKey], index) => {
      const point = points[index];
      (patch as Record<string, number | undefined>)[xKey] = point ? point.x / CANVAS_W * 100 : undefined;
      (patch as Record<string, number | undefined>)[yKey] = point ? point.y / CANVAS_H * 100 : undefined;
    });
    return patch;
  };
  const persistMidpointRoute = (drag: NonNullable<typeof midpointDragRef.current>, route: RoutePoint[]) => {
    const marker = positionedMarkers.find((candidate) => (candidate.itemIds ?? [candidate.item.id]).includes(drag.itemId));
    const memberIds = marker?.itemIds ?? [drag.itemId];
    const cluster = memberIds.length > 1;
    const savedStyle = layout[String(drag.itemId)]?.lineStyle;
    const dragItem = pageItems.find((entry) => entry.id === drag.itemId);
    if (!dragItem) return;
    const routeTarget = marker
      ? (cluster ? forkForMarker(marker, memberIds) : { x: marker.x, y: marker.y })
      : undefined;
    // Do not infer the shared-junction index from the saved card line style.
    // The rendered route may contain a newly inserted elbow or legacy bends,
    // so the saved style can describe a different number of points. Locate the
    // actual junction in the route that was dragged; this keeps the card-side
    // branch and the shared marker-side tail independent.
    const forkIndex = cluster && routeTarget
      ? route.findIndex((point) => Math.hypot(point.x - routeTarget.x, point.y - routeTarget.y) < 1)
      : route.length - 1;
    const resolvedForkIndex = cluster && forkIndex >= 0
      ? forkIndex
      : cluster
        ? bendCountForStyle(savedStyle
          ?? (routeTarget ? defaultLineStyle(anchorOf(dragItem), routeTarget) : undefined)) + 1
        : route.length - 1;
    const editingCardSide = !cluster || drag.segmentIndex < resolvedForkIndex;
    if (editingCardSide) {
      const count = Math.min(7, Math.max(0, resolvedForkIndex - 1));
      onLineLayoutChange?.(drag.itemId, {
        lineStyle: lineStyleForBendCount(count),
        ...midpointPatch(route.slice(0, resolvedForkIndex + 1), count, 'bend'),
      });
      return;
    }
    if (!marker) return;
    const markerCount = Math.min(7, Math.max(0, route.length - resolvedForkIndex - 2));
    const markerRoute = route.slice(resolvedForkIndex, route.length);
    onMarkerLineLayoutChange?.(marker.layoutKey, {
      markerRouteEdited: true,
      markerLineStyle: lineStyleForBendCount(markerCount),
      ...midpointPatch(markerRoute, markerCount, 'markerBend'),
    });
  };

  return (
    <DeckPage fixed={fixed} pageKey={page.key} backgroundImage={backgroundImage} fontFamily="'Liberation Sans', sans-serif" renderMode={renderMode}>
      <DeckHeader heading={page.heading} accent={page.accent} accentColor={page.accentColor} />
      <svg
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        aria-label="Singapore map"
      >
        <DeckMap rect={MAP_RECT} mainlandOnly />
        {/* Downstream markers remain visible on the blank canvas. They use
            the same projected/clustered positions as Overview, but retain
            the downstream unnumbered filled-circle treatment. */}
        {positionedMarkers.map((marker) => {
          const color = regionColor(marker.item.section);
          return (
            <g key={`blank-marker-${marker.item.id}`}>
              <circle cx={marker.x} cy={marker.y} r={marker.radius} fill={color} stroke={color} strokeWidth={1.4} />
            </g>
          );
        })}
      </svg>
      {(renderCards || renderConnectors) && (
      <>
      <svg
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {/* Connector routes are always visible. They are rendered before the
            markers/cards so the marker fill and card body remain readable. */}
        {(() => {
          const renderedShared = new Set<number>();
          return connectorItems.flatMap((item) => {
            const route = displayedConnectorRoutes.get(item.id);
            if (!route?.length) return [];
            const parts = connectorPartsFor(item.id, route);
            const memberIds = positionedMarkers.find((marker) => marker.item.id === parts.markerId)?.itemIds ?? [item.id];
            const groupSelected = memberIds.some((id) => selectedCardIds.has(id));
            const lines: React.ReactNode[] = [];
            if (!selectedCardIds.has(item.id) && parts.branch.length > 1) {
              lines.push(
                <polyline
                  key={`line-branch-${item.id}`}
                  points={parts.branch.map((point) => `${point.x},${point.y}`).join(' ')}
                  fill="none"
                  stroke={regionColor(item.section)}
                  strokeWidth={effectiveConnectorStrokeWidth}
                  strokeLinecap="butt"
                  strokeLinejoin="miter"
                  opacity={0.95}
                />,
              );
            }
            if (parts.sharedTail && parts.markerId != null && !groupSelected && !renderedShared.has(parts.markerId)) {
              renderedShared.add(parts.markerId);
              lines.push(
                <polyline
                  key={`line-shared-${parts.markerId}`}
                  points={parts.sharedTail.map((point) => `${point.x},${point.y}`).join(' ')}
                  fill="none"
                  stroke={regionColor(item.section)}
                  strokeWidth={effectiveConnectorStrokeWidth}
                  strokeLinecap="butt"
                  strokeLinejoin="miter"
                  opacity={0.95}
                />,
              );
            }
            return lines;
          });
        })()}
        {positionedMarkers.filter((marker) => (marker.itemIds?.length ?? 1) > 1).map((marker) => {
          const memberIds = (marker.itemIds ?? [marker.item.id]).filter((id) => pageItems.some((item) => item.id === id));
          const fork = forkForMarker(marker, memberIds);
          const selected = forkIsSelected(memberIds);
          const interactive = editable && !fixed;
          return (
            <circle
              key={`fork-${marker.item.id}`}
              cx={fork.x}
              cy={fork.y}
              // Shared-marker junctions are shown only while one of their
              // linked cards is selected, keeping the normal map uncluttered.
              // The transparent hit area remains available for interaction.
              r={selected ? 6 : 5}
              fill={selected ? '#ffffff' : 'transparent'}
              stroke={selected ? '#0b2740' : 'transparent'}
              strokeWidth={selected ? 1.4 : 0}
              style={interactive ? { pointerEvents: 'all', cursor: 'move' } : undefined}
              aria-label={interactive ? 'Drag shared project junction' : undefined}
              onPointerDown={interactive ? (event) => handleForkPointerDown(event, marker, memberIds) : undefined}
              onPointerMove={interactive ? handleForkPointerMove : undefined}
              onPointerUp={interactive ? handleForkPointerUp : undefined}
              onPointerCancel={interactive ? handleForkPointerCancel : undefined}
            />
          );
        })}
        {positionedMarkers.map((marker) => {
          const color = regionColor(marker.item.section);
          return (
            <g key={`dot-${marker.item.id}`}>
              <circle
                cx={marker.x}
                cy={marker.y}
                r={marker.radius}
                fill={color}
                stroke={color}
                strokeWidth={1.4}
                style={editable && !fixed && onMarkerMove ? { pointerEvents: 'all', cursor: 'move' } : undefined}
                aria-label={editable && !fixed && onMarkerMove ? 'Drag project marker' : undefined}
                onPointerDown={editable && !fixed && onMarkerMove ? (event) => handleMarkerPointerDown(event, marker) : undefined}
                onPointerMove={editable && !fixed && onMarkerMove ? handleMarkerPointerMove : undefined}
                onPointerUp={editable && !fixed && onMarkerMove ? handleMarkerPointerUp : undefined}
                onPointerCancel={editable && !fixed && onMarkerMove ? handleMarkerPointerCancel : undefined}
              />
            </g>
          );
        })}
      </svg>

      {pageItems.map((item) => {
        const pos = positionOf(item);
        const saved = layout[String(item.id)];
        const cardWidth = saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth;
        const cardHeight = saved?.height != null ? saved.height / 100 * CANVAS_H : defaultCardHeight;
        // Use one shared Residential-sized footprint as the 1x reference.
        // Do not reset the header to 14px/6px for every page's generated
        // default: less crowded tabs begin with larger cards and therefore
        // need proportionally larger headers from their first render.
        const cardScale = Math.min(
          cardWidth / MIN_CARD_W,
          cardHeight / MIN_CARD_H,
        );
        const headerScale = Math.min(2, Math.max(0.4, cardScale));
        return (
            <DeckCard
            key={item.id}
            item={item}
            x={pos.x}
            y={pos.y}
            width={cardWidth}
            height={cardHeight}
            headerScale={headerScale}
            aspectLocked={saved?.aspectLocked ?? false}
            draggable={editable && !fixed}
            onDragMove={(id, x, y) => moveCardSafely(id, x, y, false)}
            onDragEnd={(id, x, y) => moveCardSafely(id, x, y, true)}
            onDragCancel={() => {
              multiDragRef.current = null;
              setCardAlignmentGuides(null);
            }}
            onResizeStart={beginCardResize}
            onResizeEnd={endCardResize}
            dataEditable={editingItemId === item.id}
            onItemChange={onPageItemChange}
            allowedSections={[]}
            headerColor={regionColor(item.section)}
            launchingSoonColor={regionColorLayout.legend?.launchingSoonColor}
            showLaunchingSoonBorder={false}
            onContextMenu={(id, event) => {
              if (!editable || fixed) return;
              event.preventDefault();
              event.stopPropagation();
              setLineMenu({ itemId: id, top: event.clientY, left: event.clientX });
            }}
            onDoubleClick={(_id, event) => {
              event.preventDefault();
              event.stopPropagation();
              setEditingItemId(item.id);
            }}
            onEditEnd={(id) => setEditingItemId((current) => current === id ? null : current)}
            onSizeChange={handleCardSizeChange}
            onPrintMeasure={renderMode === 'print' ? handlePrintMeasure : undefined}
            onResize={resizeCardSafely}
            displaySettings={cardDisplayForItem(item.id)}
            selected={selectedCardIds.has(item.id)}
            onSelect={selectCard}
            renderMode={renderMode}
          />
        );
      })}

      {cardAlignmentGuides && (
        <svg
          width={CANVAS_W}
          height={CANVAS_H}
          viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
          aria-label="Card alignment guides"
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 50 }}
        >
          {cardAlignmentGuides.vertical != null && (
            <line
              x1={cardAlignmentGuides.vertical}
              y1={0}
              x2={cardAlignmentGuides.vertical}
              y2={CANVAS_H}
              stroke="#ff5fc7"
              strokeWidth={1.5}
              strokeDasharray="6 5"
            />
          )}
          {cardAlignmentGuides.horizontal != null && (
            <line
              x1={0}
              y1={cardAlignmentGuides.horizontal}
              x2={CANVAS_W}
              y2={cardAlignmentGuides.horizontal}
              stroke="#ff5fc7"
              strokeWidth={1.5}
              strokeDasharray="6 5"
            />
          )}
        </svg>
      )}

      {PIPELINE_PDF_DEBUG && renderMode === 'print' && (
        <svg
          width={CANVAS_W}
          height={CANVAS_H}
          viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
          aria-label="Print connector diagnostics"
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 60 }}
        >
          {pageItems.map((item) => {
            const position = positionOf(item);
            const saved = layout[String(item.id)];
            const width = saved?.width != null ? saved.width / 100 * CANVAS_W : defaultCardWidth;
            const height = printCardMeasurements[item.id] ?? cardHeights[item.id] ?? defaultCardHeight;
            const anchor = anchorOf(item);
            const route = displayedConnectorRoutes.get(item.id);
            return (
              <g key={`print-debug-${item.id}`}>
                <rect x={position.x} y={position.y} width={width} height={height} fill="none" stroke="#ff00ff" strokeWidth={2} />
                <circle cx={anchor.x} cy={anchor.y} r={5} fill="#00ff66" stroke="#000000" strokeWidth={1} />
                {route?.length ? <>
                  <circle cx={route[0].x} cy={route[0].y} r={4} fill="#00ffff" stroke="#000000" strokeWidth={1} />
                  <circle cx={route[route.length - 1].x} cy={route[route.length - 1].y} r={4} fill="#ff9900" stroke="#000000" strokeWidth={1} />
                </> : null}
              </g>
            );
          })}
        </svg>
      )}

      {/* Selected connectors are promoted above cards so the active line stays
          visible while editing. Once selection changes, the previous line is
          rendered by the background connector layer again. */}
      {renderConnectors && selectedCardIds.size > 0 && (
        <svg
          width={CANVAS_W}
          height={CANVAS_H}
          viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 19 }}
        >
          {/* Draw the selected route first so midpoint pills always remain
              visibly above the highlighted connector stroke. */}
          {(() => {
            const renderedShared = new Set<number>();
            return connectorItems.filter((item) => item.id === activeConnectorCardId).flatMap((item) => {
              const route = displayedConnectorRoutes.get(item.id);
              if (!route?.length) return [];
              const parts = connectorPartsFor(item.id, route);
              const lines: React.ReactNode[] = [];
              if (parts.branch.length > 1) {
                lines.push(
                  <polyline
                    key={`selected-line-branch-${item.id}`}
                    points={parts.branch.map((point) => `${point.x},${point.y}`).join(' ')}
                    fill="none"
                    stroke="#f5d76e"
                    strokeWidth={Math.max(CONNECTOR_SELECTED_STROKE_WIDTH, effectiveConnectorStrokeWidth + 2.5)}
                    strokeLinecap="butt"
                    strokeLinejoin="miter"
                    opacity={1}
                  />,
                );
              }
              if (parts.sharedTail && parts.markerId != null && !renderedShared.has(parts.markerId)) {
                renderedShared.add(parts.markerId);
                lines.push(
                  <polyline
                    key={`selected-line-shared-${parts.markerId}`}
                    points={parts.sharedTail.map((point) => `${point.x},${point.y}`).join(' ')}
                    fill="none"
                    stroke="#f5d76e"
                    strokeWidth={Math.max(CONNECTOR_SELECTED_STROKE_WIDTH, effectiveConnectorStrokeWidth + 2.5)}
                    strokeLinecap="butt"
                    strokeLinejoin="miter"
                    opacity={1}
                  />,
                );
              }
              return lines;
            });
          })()}
          {/* Canva-style midpoint controls. Each selected route segment gets
              one handle; dragging previews an orthogonal detour locally and
              persists it only when the pointer is released. */}
          {editable && !fixed && renderMode !== 'print' && (() => {
            const renderedSegments: Array<{ owner: string; horizontal: boolean; axis: number; start: number; end: number }> = [];
            const renderedSharedSegments = new Set<string>();
            return connectorItems.filter((item) => item.id === activeConnectorCardId).flatMap((item) => {
            const route = displayedConnectorRoutes.get(item.id);
            if (!route || route.length < 2) return [];
            const marker = positionedMarkers.find((candidate) => (candidate.itemIds ?? [candidate.item.id]).includes(item.id));
            const memberIds = marker?.itemIds ?? [item.id];
            const isSharedMarker = memberIds.length > 1;
            const routeParts = connectorPartsFor(item.id, route);
            // Use the same split route that is painted above. The controls
            // must never be calculated from a stale bend count, otherwise a
            // pill can edit the wrong side of the junction or bring the old
            // full route back into the preview.
            const controlRoute = isSharedMarker && routeParts.sharedTail
              ? [...routeParts.branch, ...routeParts.sharedTail.slice(1)]
              : route;
            const forkIndex = isSharedMarker ? routeParts.branch.length - 1 : -1;
            const protectedBreaks = isSharedMarker ? new Set([forkIndex]) : undefined;
            return uniqueRouteSegments(controlRoute, protectedBreaks).flatMap(({ start, end, segmentIndex, endIndex }) => {
              const horizontal = Math.abs(start.y - end.y) < 0.5;
              const axis = horizontal ? (start.y + end.y) / 2 : (start.x + end.x) / 2;
              const rangeStart = horizontal ? Math.min(start.x, end.x) : Math.min(start.y, end.y);
              const rangeEnd = horizontal ? Math.max(start.x, end.x) : Math.max(start.y, end.y);
              if (rangeEnd - rangeStart < MIN_MIDPOINT_HANDLE_LENGTH) return [];
              const isSharedTailSegment = isSharedMarker && segmentIndex >= forkIndex;
              const owner = isSharedTailSegment ? `shared-marker-${marker!.item.id}` : `card-${item.id}`;
              // The shared tail is one control set for the whole marker group.
              // Card branches retain their own controls even when their pixels
              // overlap or align with the shared tail.
              const sharedSegmentKey = isSharedTailSegment
                ? `${Math.round(start.x * 2) / 2},${Math.round(start.y * 2) / 2}-${Math.round(end.x * 2) / 2},${Math.round(end.y * 2) / 2}`
                : '';
              if (isSharedTailSegment && renderedSharedSegments.has(sharedSegmentKey)) return [];
              const overlaps = renderedSegments.some((existing) => existing.horizontal === horizontal
                && existing.owner === owner
                && Math.abs(existing.axis - axis) < 1
                && Math.min(existing.end, rangeEnd) - Math.max(existing.start, rangeStart) > 4);
              if (overlaps) return [];
              renderedSegments.push({ owner, horizontal, axis, start: rangeStart, end: rangeEnd });
              if (isSharedTailSegment) renderedSharedSegments.add(sharedSegmentKey);
              const midpoint = midpointForSegment(start, end);
              // The junction is the fixed start of the marker-side route. A
              // drag on that first downstream segment may add/move bends, but
              // must never move the fork and therefore must not move any card
              // branch attached to it.
              const preservesJunction = isSharedMarker && segmentIndex === forkIndex;
              const toCanvasPoint = (event: React.PointerEvent<SVGElement>): RoutePoint | null => {
                const canvas = event.currentTarget.closest('[data-pipeline-page]') as HTMLElement | null;
                if (!canvas) return null;
                const rect = canvas.getBoundingClientRect();
                return {
                  x: Math.max(0, Math.min(CANVAS_W, (event.clientX - rect.left) / rect.width * CANVAS_W)),
                  y: Math.max(0, Math.min(CANVAS_H, (event.clientY - rect.top) / rect.height * CANVAS_H)),
                };
              };
              return [(
                <rect
                  key={`midpoint-overlay-${item.id}-${segmentIndex}`}
                  x={midpoint.x - (horizontal ? 7 : 2.5)}
                  y={midpoint.y - (horizontal ? 2.5 : 7)}
                  width={horizontal ? 14 : 5}
                  height={horizontal ? 5 : 14}
                  rx={2}
                  fill="#63e6ff"
                  stroke="#0b2740"
                  strokeWidth={1}
                  style={{ pointerEvents: 'all', cursor: 'grab' }}
                  aria-label="Drag connector midpoint to add bend"
                  onPointerDown={(event) => {
                    event.currentTarget.setPointerCapture(event.pointerId);
                    const sharedMarkerEdit = isSharedMarker && segmentIndex >= forkIndex;
                    midpointDragRef.current = {
                      itemId: item.id,
                      segmentIndex,
                      pointerId: event.pointerId,
                      originalRoute: controlRoute.map((entry) => ({ ...entry })),
                      hadLocalRoute: localRoutes.has(item.id),
                      sharedMarkerId: sharedMarkerEdit ? marker!.item.id : undefined,
                      originalSharedTail: sharedMarkerEdit
                        ? routeParts.sharedTail?.map((point) => ({ ...point }))
                        : undefined,
                    };
                  }}
                  onPointerMove={(event) => {
                    const drag = midpointDragRef.current;
                    if (!drag || drag.itemId !== item.id || drag.segmentIndex !== segmentIndex || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
                    const pointer = toCanvasPoint(event);
                    if (!pointer) return;
                    const preview = routeWithMidpointBend(
                      drag.originalRoute,
                      segmentIndex,
                      pointer,
                      endIndex,
                      preservesJunction,
                      isSharedMarker,
                    );
                    if (isSharedMarker && segmentIndex >= forkIndex) {
                      // Marker-side bends belong to one shared preview keyed
                      // by marker, never to the selected card's local route.
                      setSharedTailPreviews((current) => new Map(current).set(
                        marker!.item.id,
                        preview.slice(forkIndex).map((point) => ({ ...point })),
                      ));
                    } else {
                      setLocalRoutes((current) => new Map(current).set(item.id, preview));
                    }
                  }}
                  onPointerUp={(event) => {
                    const drag = midpointDragRef.current;
                    if (!drag || drag.itemId !== item.id || drag.segmentIndex !== segmentIndex) return;
                    const pointer = toCanvasPoint(event);
                    const preview = pointer
                      ? routeWithMidpointBend(
                        drag.originalRoute,
                        segmentIndex,
                        pointer,
                        endIndex,
                        preservesJunction,
                        isSharedMarker,
                      )
                      : drag.originalRoute;
                    persistMidpointRoute(drag, preview);
                    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
                    midpointDragRef.current = null;
                  }}
                  onPointerCancel={(event) => {
                    const drag = midpointDragRef.current;
                    if (!drag || drag.itemId !== item.id || drag.segmentIndex !== segmentIndex) return;
                    if (drag.sharedMarkerId != null) {
                      setSharedTailPreviews((current) => {
                        const next = new Map(current);
                        if (drag.originalSharedTail) next.set(drag.sharedMarkerId!, drag.originalSharedTail);
                        else next.delete(drag.sharedMarkerId!);
                        return next;
                      });
                    } else setLocalRoutes((current) => {
                      const next = new Map(current);
                      if (drag.hadLocalRoute) next.set(item.id, drag.originalRoute);
                      else next.delete(item.id);
                      return next;
                    });
                    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
                    midpointDragRef.current = null;
                  }}
                />
              )];
            });
            });
          })()}
        </svg>
      )}

      <Menu
        open={lineMenu != null}
        onClose={() => setLineMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={lineMenu ? { top: lineMenu.top, left: lineMenu.left } : undefined}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onPointerDown={(event) => event.stopPropagation()}
        MenuListProps={{ dense: true, autoFocusItem: false, 'aria-label': 'Connecting line options' }}
      >
        <MenuItem
          onClick={() => {
            if (lineMenu) {
              onLineLayoutChange?.(lineMenu.itemId, { aspectLocked: !(selectedLineLayout?.aspectLocked ?? false) });
            }
            setLineMenu(null);
          }}
        >
          {(selectedLineLayout?.aspectLocked ?? false) ? 'Unlock card aspect ratio' : 'Lock card aspect ratio'}
        </MenuItem>
        <Divider />
        <ListSubheader>Connect to card side</ListSubheader>
        {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
          <MenuItem
            key={side}
            selected={selectedAnchorSide === side}
            onClick={() => {
              if (lineMenu) {
                const nextRoute = recalculateRouteForSide(lineMenu.itemId, side);
                const item = pageItems.find((candidate) => candidate.id === lineMenu.itemId);
                const cardBendCount = nextRoute && item
                  ? Math.max(0, nextRoute.length - 2)
                  : 0;
                if (nextRoute) {
                  setLocalRoutes((current) => new Map(current).set(lineMenu.itemId, nextRoute));
                }
                onLineLayoutChange?.(lineMenu.itemId, {
                  anchorSide: side,
                  anchorOffset: 50,
                  lineStyle: lineStyleForBendCount(Math.min(7, cardBendCount)),
                  ...bendLayoutPatch(nextRoute, Math.min(7, cardBendCount)),
                });
              }
              setLineMenu(null);
            }}
          >
            {side[0].toUpperCase() + side.slice(1)} side
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={cardSettingsOpen} onClose={onCardSettingsClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Card fields — {page.label}
          <IconButton
            aria-label="Close card field settings"
            onClick={onCardSettingsClose}
            sx={{ position: 'absolute', right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These settings apply to the selected card or selected cards. Select a card to edit its formatting.
          </Typography>
          {CARD_FIELD_OPTIONS.map(({ key, label, defaultSize, defaultBold = false }) => {
            const defaultVisible = DEFAULT_VISIBLE_CARD_FIELDS.includes(key);
            const selectedStyles = selectedCardIdList.map((itemId) => cardDisplayForItem(itemId).fields?.[key] ?? {});
            const fallbackStyle = cardDisplay.fields?.[key] ?? {};
            const settings = selectedStyles[0] ?? fallbackStyle;
            const values = {
              fontSize: settings.fontSize ?? defaultSize,
              color: settings.color ?? (key === 'targetPreview' ? '#e01f1f' : '#000000'),
              highlight: settings.highlight ?? '#ffffff',
              bold: settings.bold ?? defaultBold,
              italic: settings.italic ?? false,
              visible: settings.visible ?? defaultVisible,
            };
            const mixed = (field: keyof typeof values) => selectedStyles.length > 1
              && selectedStyles.some((style) => (style[field] ?? values[field]) !== values[field]);
            const hasSelection = selectedCardIdList.length > 0;
            const fontSizeMixed = mixed('fontSize');
            const boldMixed = mixed('bold');
            const italicMixed = mixed('italic');
            const visibleMixed = mixed('visible');
            const colorMixed = mixed('color');
            const highlightMixed = mixed('highlight');
            return (
              <Box
                key={key}
                sx={{ display: 'grid', gridTemplateColumns: '190px 90px 90px 90px 110px 100px 1fr', gap: 1, alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box component="label" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <Checkbox
                    disabled={!hasSelection}
                    indeterminate={visibleMixed}
                    checked={values.visible}
                    onChange={(event) => patchCardField(key, { visible: event.target.checked })}
                  />
                  {label}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="input" type="color" title={colorMixed ? 'Multiple text colours' : 'Text colour'} disabled={!hasSelection} value={colorMixed ? '#808080' : values.color} onChange={(event: React.ChangeEvent<HTMLInputElement>) => patchCardField(key, { color: event.target.value })} sx={{ width: 32, height: 28, border: 0, p: 0 }} />
                  <Typography variant="caption">Text</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="input" type="color" title={highlightMixed ? 'Multiple highlight colours' : 'Highlight colour'} disabled={!hasSelection} value={highlightMixed ? '#808080' : values.highlight} onChange={(event: React.ChangeEvent<HTMLInputElement>) => patchCardField(key, { highlight: event.target.value })} sx={{ width: 32, height: 28, border: 0, p: 0 }} />
                  <Typography variant="caption">Highlight</Typography>
                </Box>
                <Box>
                  <IconButton size="small" disabled={!hasSelection} title={boldMixed ? 'Multiple bold values' : 'Bold'} aria-pressed={values.bold} onClick={() => patchCardField(key, { bold: !values.bold })} sx={{ border: '1px solid', borderColor: values.bold && !boldMixed ? 'primary.main' : 'divider', bgcolor: values.bold && !boldMixed ? 'primary.main' : 'transparent', color: values.bold && !boldMixed ? 'primary.contrastText' : 'text.primary', '&:hover': { bgcolor: values.bold && !boldMixed ? 'primary.dark' : 'action.hover' } }}><FormatBoldIcon /></IconButton>
                  <IconButton size="small" disabled={!hasSelection} title={italicMixed ? 'Multiple italic values' : 'Italic'} aria-pressed={values.italic} onClick={() => patchCardField(key, { italic: !values.italic })} sx={{ ml: 0.5, border: '1px solid', borderColor: values.italic && !italicMixed ? 'primary.main' : 'divider', bgcolor: values.italic && !italicMixed ? 'primary.main' : 'transparent', color: values.italic && !italicMixed ? 'primary.contrastText' : 'text.primary', '&:hover': { bgcolor: values.italic && !italicMixed ? 'primary.dark' : 'action.hover' } }}><FormatItalicIcon /></IconButton>
                </Box>
                <TextField
                  size="small"
                  type="text"
                  label="Font size"
                  disabled={!hasSelection}
                  placeholder={fontSizeMixed ? 'Multiple' : undefined}
                  value={fontSizeDrafts[key] ?? (fontSizeMixed ? '' : String(values.fontSize))}
                  inputProps={{ inputMode: 'decimal', min: 6, max: 24, step: 0.1 }}
                  onFocus={() => {
                    setFontSizeDrafts((current) => current[key] === undefined ? { ...current, [key]: fontSizeMixed ? '' : String(values.fontSize) } : current);
                  }}
                  onChange={(event) => {
                    setFontSizeDrafts((current) => ({ ...current, [key]: event.target.value }));
                  }}
                  onBlur={() => {
                    commitCardFontSize(key, fontSizeDrafts[key] ?? String(values.fontSize), values.fontSize);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitCardFontSize(key, fontSizeDrafts[key] ?? String(values.fontSize), values.fontSize);
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, whiteSpace: 'nowrap' }}>Default {defaultSize}px</Typography>
                <Button
                  size="small"
                  disabled={!hasSelection}
                  onClick={() => {
                    setFontSizeDrafts((current) => {
                      if (!(key in current)) return current;
                      const next = { ...current };
                      delete next[key];
                      return next;
                    });
                    patchCardField(key, {
                      color: undefined,
                      highlight: undefined,
                      bold: undefined,
                      italic: undefined,
                      fontSize: undefined,
                      visible: undefined,
                    });
                  }}
                >
                  Reset formatting
                </Button>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!selectedCardIdList.length}
            onClick={() => {
              setFontSizeDrafts({});
              const fields = Object.fromEntries(CARD_FIELD_OPTIONS.map(({ key }) => [key, {
                color: undefined,
                highlight: undefined,
                bold: undefined,
                italic: undefined,
                fontSize: undefined,
                visible: undefined,
              }])) as CardDisplaySettings['fields'];
              onCardDisplayChange?.({ fields }, selectedCardIdList);
            }}
          >Reset all selected</Button>
          <Button variant="contained" onClick={onCardSettingsClose}>Done</Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      <AdjustableMapLegend
        x={legendX}
        y={legendY}
        w={legendW}
        editable={editable && !fixed}
        projectNameKeyAbove
        launchingSoonColor={regionColorLayout.legend?.launchingSoonColor}
        launchingSoonText={regionColorLayout.legend?.launchingSoonText}
        onChange={(x, y, width) => onLegendChange?.(
          x / CANVAS_W * 100,
          y / CANVAS_H * 100,
          width / CANVAS_W * 100,
        )}
      />
      {/* Downstream disclaimers sit close to the canvas edge while retaining a
          small inset from the border. Overview keeps the standard footer
          position. */}
      <DeckFooter asAtDate={asAtDate} bottom={8} compact />

      {pageItems.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 22,
            fontWeight: 600,
            pointerEvents: 'none',
          }}
        >
          No projects assigned to this page yet — generate the deck from the Input tab.
        </Box>
      )}
    </DeckPage>
  );
};
